<template>
  <v-container class="chat-wrapper" fluid>
    <!-- Model Select -->
    <!-- <v-row>
      <v-card-title class="model-select">
        <v-select 
          v-model="selectedModel" 
          variant="solo" 
          class="custom-field" 
          :items="models" 
          item-value="name"
          item-title="company" 
          placeholder="Select a Model" 
          density="compact"
          :menu-props="{ disabled: true }"
          :readonly="true"
        >
          <template #item="{ props, item }">
            <v-list-item v-bind="props" class="d-flex align-center">
              <v-avatar left size="24" class="mr-2">
                <img :src="item.raw.icon" alt="icon" />
              </v-avatar>
              <span>{{ item.raw.name }}</span>
            </v-list-item>
          </template>

<template #selection="{ item }">
            <div class="d-flex align-center">
              <v-avatar size="24" class="mr-2">
                <img :src="item.raw.icon" alt="icon" />
              </v-avatar>
              <span>{{ item.raw.name }}</span>
            </div>
          </template>
</v-select>

</v-card-title>
<v-divider class="divider"></v-divider>
</v-row> -->

    <!-- Chat History -->
    <v-row v-if="!getFirstMsg" class="chat-area">
      <v-col cols="12">
        <!-- Responsive height for chat card -->
        <v-card :style="chatCardStyle" class="d-flex flex-column">
          <!-- Chat History Scrollable -->
          <div class="chat-history flex-grow-1" ref="chatHistory" 
               @click="handleChatClick"
               @click.capture="handleChatClickCapture"
               @mousedown="handleMouseDown"
               @mousedown.capture="handleMouseDownCapture"
               @mouseup="handleMouseUp"
               @mouseup.capture="handleMouseUpCapture"
               @mousemove="handleMouseMove"
               @wheel="handleWheel"
               @scroll="handleScroll">
            <!-- Loading indicator for history -->
            <div v-if="historyLoading" class="history-loading">
              <v-progress-circular indeterminate color="primary" size="40"></v-progress-circular>
              <p class="loading-text">Loading chat history...</p>
            </div>
            <v-list v-else-if="history.length > 0" dense>
              <v-list-item v-for="(msg, index) in history" :key="index">
                <v-list-item-content>
                  <div v-if="msg.role === 'user'" class="profile-Logo">
                    {{ userInitials }}
                  </div>
                  <div v-if="msg.role === 'ai'" class="ai-Logo">
                    <img :src="aiLogo" alt="AI Logo" />
                  </div>
                  <div v-if="msg.role === 'user'" class="right chat-bubble">
                    <div v-html="renderMarkdown(msg.context)"></div>
                  </div>
                  
                  <div v-else class="left chat-bubble" :class="{ 'full-width-studio': msg.studioEmbedded }">
                    <!-- Studio Agent - Plot Link and Prompt -->
                    <div v-if="msg.metadata && (msg.metadata.route === 'StudioAgent')" class="studio-plot-link">
                      <!-- Show prompt if studio not embedded yet -->
                      <div v-if="!msg.studioEmbedded && !msg.studioResponded" class="studio-prompt">
                        <div class="studio-question">
                          <p class="question-text">Do you want to open Studio with this plot in chat?</p>
                          <div class="question-buttons">
                            <v-btn 
                              small 
                              @click="handleStudioYes(index)"
                              class="yes-btn">
                              Yes
                            </v-btn>
                            <v-btn 
                              small 
                              outlined 
                              @click="handleStudioNo(index)"
                              class="no-btn">
                              No
                            </v-btn>
                          </div>
                        </div>
                        <!-- Also show link to open in new page -->
                        <div class="studio-link-container">
                          <a 
                            :href="getStudioPlotUrl(msg.context)" 
                            @click.prevent="openStudioInNewPage(msg.context)"
                            class="studio-link">
                            <v-icon small color="primary" class="fd-intel-icon-gap">mdi-open-in-new</v-icon>
                            <span>Open in Studio Page</span>
                          </a>
                        </div>
                      </div>
                      <!-- Show embedded studio if user clicked yes -->
                      <div v-else-if="msg.studioEmbedded" class="embedded-studio-container" 
                           @mouseenter="disableScrollPreventionForStudio"
                           @focusin="disableScrollPreventionForStudio"
                           @click.stop="disableScrollPreventionForStudio"
                           @mousedown.stop="disableScrollPreventionForStudio"
                           @touchstart.stop="disableScrollPreventionForStudio"
                           @focus="disableScrollPreventionForStudio">
                       
                        <div class="embedded-studio-content" 
                             @mouseenter="disableScrollPreventionForStudio"
                             @focusin="disableScrollPreventionForStudio"
                             @click.stop="disableScrollPreventionForStudio"
                             @mousedown.stop="disableScrollPreventionForStudio"
                             @focus="disableScrollPreventionForStudio">
                          <StudioData :plotId="getPlotIdFromContext(msg.context)" :embedded="true" />
                        </div>
                        <div class="embedded-studio-footer">
                          <a 
                            :href="getStudioPlotUrl(msg.context)" 
                            @click.prevent.stop="openStudioInNewPage(msg.context)"
                            class="studio-link-footer">
                            <v-icon small color="primary" class="fd-intel-icon-gap">mdi-open-in-new</v-icon>
                            <span>Open {{ getPlotName(msg.context) }} in Studio Page</span>
                          </a>
                        </div>
                      </div>
                      <!-- Show link if user clicked no -->
                      <div v-else-if="msg.studioResponded && !msg.studioEmbedded" class="studio-link-container">
                        <a 
                          :href="getStudioPlotUrl(msg.context)" 
                          @click.prevent="openStudioInNewPage(msg.context)"
                          class="studio-link">
                          <v-icon small color="primary" class="fd-intel-icon-gap">mdi-chart-scatter-plot</v-icon>
                          <span>{{ getPlotName(msg.context) }}</span>
                        </a>
                      </div>
                    </div>
                    <!-- Text content -->
                    <div v-else-if="!msg.type || msg.type === 'text'" v-html="renderMarkdown(msg.context)"></div>
                    <!-- Plot content (iframe with blob URL) -->
                    <div v-if="msg.type === 'plot' && msg.plotUrl" class="html-content">
                      <div v-if="msg.plotLoading" class="plot-loading">
                        <v-progress-circular indeterminate color="primary" size="40"></v-progress-circular>
                        <p>Loading plot from storage...</p>
                        <p class="plot-loading-hint">This may take a few moments</p>
                      </div>
                      <div v-if="msg.plotError" class="plot-error">
                        <p>Error loading plot. <a :href="msg.plotUrl" target="_blank" rel="noopener noreferrer">Open in new tab</a></p>
                      </div>
                      <iframe 
                      :src="msg.plotUrl" 
                      class="plot-iframe"
                      frameborder="0"
                      scrolling="auto"
                      allow="scripts"
                      referrerpolicy="no-referrer-when-downgrade"
                      @load="handlePlotLoad(index)"
                      @error="handlePlotError(index)"
                      :style="{
                        visibility: (!msg.plotLoading && !msg.plotError) ? 'visible' : 'hidden'
                      }"
                    >
                    </iframe>
                    </div>
                    <!-- HTML content (iframe for plots - legacy format) -->
                    <div v-else-if="msg.type === 'html'" class="html-content">
                      <iframe 
                        :srcdoc="msg.context" 
                        class="plot-iframe"
                        frameborder="0"
                        scrolling="no"
                        sandbox="allow-scripts allow-same-origin"
                        @load="adjustIframeHeight">
                      </iframe>
                    </div>
                    <!-- Visualization Agent - Plot Creation Prompt (shown below HTML chart) -->
                    <div v-if="((msg.metadata && msg.metadata.route === 'visualizationAgent') || (msg.route === 'visualizationAgent')) && !msg.visualizationResponded" 
                         class="visualization-prompt">
                      <div class="visualization-question">
                        <p class="question-text">Do you want to create this plot in studio?</p>
                        <div class="question-buttons">
                          <v-btn 
                            small 
                            @click="handleVisualizationYes(index)"
                            class="yes-btn">
                            Yes
                          </v-btn>
                          <v-btn 
                            small 
                            outlined 
                            @click="handleVisualizationNo(index)"
                            class="no-btn">
                            No
                          </v-btn>
                        </div>
                      </div>
                    </div>
                    <div class="feedback-icons fd-intel-feedback-icons">
                      <img @click="handleFeedback(index, true)" 
                           :src="likeImg" 
                           alt="Thumbs Up"
                           :class="{ 'active-feedback': likeIndex === index }"
                           class="fd-intel-feedback-icon" />
                      <img @click="handleFeedback(index, false)" 
                           :src="dislikeImg" 
                           alt="Thumbs Down"
                           :class="{ 'active-feedback': dislikeIndex === index }"
                           class="fd-intel-feedback-icon" />
                    </div>
                    
                    <!-- Feedback Form - appears below the message -->
                    <div v-if="dislikeIndex === index" class="feedback-form-below">
                      <div class="feedback-input-container">
                        <textarea v-model="feedbackText"
                          class="feedbackForm"
                          placeholder="What went wrong" rows="3"></textarea>
                        <button @click="closeFeedback" class="feedback-close-btn">✕</button>
                      </div>
                      <button @click="submitFeedback(index)" class="feedbackButton">
                        Submit Feedback
                      </button>
                    </div>
                  </div>

                </v-list-item-content>
              </v-list-item>
            </v-list>
          </div>

          <!-- Input Area -->
          <div class="input-wrapper">
            <div class="inner-chat-input-container" 
                 @dragover="handleDragOver" 
                 @drop="handleDrop"
                 :class="{ 'drag-over': isDragOver, 'disabled': loading }"
                 @dragenter="isDragOver = true"
                 @dragleave="isDragOver = false">
              <!-- File Upload Icon (Paperclip) -->
              <div class="inner-file-upload-icon" @click.stop="triggerFileUpload" :class="{ 'disabled': loading }">
                <img :src="require('@/assets/Images/FDPal/paperclip-2.svg')" alt="Upload File" />
              </div>
              
              <v-text-field v-model="message" placeholder="Ask a question about fluids characterization data, or drag and drop your PVT pdf file..." hide-details variant="solo" flat rounded
                class="custom-chat-input" @keyup.enter="sendMessage">
                <template #append-inner>
                  <v-progress-circular v-if="loading" indeterminate color="primary"></v-progress-circular>
                  <img v-else :src="sendImg" alt="Send" class="send-icon" @click="sendMessage" />
                </template>
              </v-text-field>
            </div>
            
            <!-- Hidden file input for inner chat -->
            <input 
              type="file" 
              ref="innerFileInput" 
              @change="handleFileSelect" 
              accept=".pdf" 
              class="fd-intel-hidden-file"
            />
            
            <!-- Helper text when PDF is uploaded -->
            <div v-if="fileName && falsePDF && !pdfContextUsed" class="pdf-context-helper">
              <small class="text-muted">
                <v-icon small color="info">info</v-icon>
                PDF file uploaded successfully. You can now ask questions about this report.
              </small>
            </div>
          </div>
          <!-- Suggestion Buttons - Always visible -->
          <div class="button-row">
            <!-- Buttons when no PDF is uploaded -->
            <template v-if="!isPDFUploaded">
              <v-btn outlined small 
                     :class="{ 'active-prompt': activePromptButton === 'summarize' }"
                     :disabled="loading"
                     @click="handlePromptClick('summarize', 'Summarize data from database for well: (add your well name here)')">
                  <img :src="require('@/assets/Images/FDPal/icons/summarize.svg')" alt="Summarize" />
                  Summarize data
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'samples' }"
                       :disabled="loading"
                       @click="handlePromptClick('samples', 'How many samples were collected from database for well: (add your well name). Provide me the details of the samples from this well.')">
                  <img :src="require('@/assets/Images/FDPal/icons/samples.svg')" alt="# of samples" />
                  No. of samples
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'api' }"
                       :disabled="loading"
                       @click="handlePromptClick('api', 'What is the API of the samples from database for well: (add your well name).')">
                  <img :src="require('@/assets/Images/FDPal/icons/api.svg')" alt="API" />
                  API
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'gor' }"
                       :disabled="loading"
                       @click="handlePromptClick('gor', 'What is the GOR of the samples from database for well: (add your well name).')">
                  <img :src="require('@/assets/Images/FDPal/icons/bubble-point.svg')" alt="GOR" />
                  GOR
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'bubble' }"
                       :disabled="loading"
                       @click="handlePromptClick('bubble', 'What is the bubble point pressure of the samples from database for well: (add your well name).')">
                  <img :src="require('@/assets/Images/FDPal/icons/bubble-point.svg')" alt="Bubble point" />
                  Bubble Point
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'dew' }"
                       :disabled="loading"
                       @click="handlePromptClick('dew', 'What is the dew point pressure of the samples from database for well: (add your well name).')">
                  <img :src="require('@/assets/Images/FDPal/icons/bubble-point.svg')" alt="Dew point" />
                  Dew Point
                </v-btn>
            </template>
            
            <!-- Buttons when PDF is uploaded -->
            <template v-else>
              <v-btn outlined small 
                     :class="{ 'active-prompt': activePromptButton === 'summarize' }"
                     :disabled="loading"
                     @click="handlePromptClick('summarize', 'Summarize this PVT report.')">
                  <img :src="require('@/assets/Images/FDPal/icons/summarize.svg')" alt="Summarize" />
                  Summarize Report
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'samples' }"
                       :disabled="loading"
                       @click="handlePromptClick('samples', 'How many samples were collected for this study? Provide me the details of the samples in this study.')">
                  <img :src="require('@/assets/Images/FDPal/icons/samples.svg')" alt="# of samples" />
                  No. of samples
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'api' }"
                       :disabled="loading"
                       @click="handlePromptClick('api', 'What is the API of the fluid in this report?')">
                  <img :src="require('@/assets/Images/FDPal/icons/api.svg')" alt="API" />
                  API
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'gor' }"
                       :disabled="loading"
                       @click="handlePromptClick('gor', 'What is the GOR of the samples in this PVT study?')">
                  <img :src="require('@/assets/Images/FDPal/icons/bubble-point.svg')" alt="GOR" />
                  GOR
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'bubble' }"
                       :disabled="loading"
                       @click="handlePromptClick('bubble', 'What is the bubble point pressure of the samples in this PVT study?')">
                  <img :src="require('@/assets/Images/FDPal/icons/bubble-point.svg')" alt="Bubble point" />
                  Bubble point
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'dew' }"
                       :disabled="loading"
                       @click="handlePromptClick('dew', 'What is the dew point pressure of the samples in this PVT study?')">
                  <img :src="require('@/assets/Images/FDPal/icons/bubble-point.svg')" alt="Dew point" />
                  Dew point
                </v-btn>
            </template>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Initial Empty View -->
    <v-row v-else justify="center" align="center" class="fill-height">
      <v-col cols="12" md="auto">
        
        <v-card class="chat-card" elevation="3">
          <div>
            <!-- Loading indicator for history -->
            <div v-if="historyLoading" class="first-screen-loading">
              <v-progress-circular indeterminate color="primary" size="50"></v-progress-circular>
              <p class="loading-text">Loading chat history...</p>
            </div>
            
            <!-- Title -->
            <h1 v-if="!historyLoading" class="chat-title">Let's build something, {{ userName }}?</h1>
            
            <!-- Initial Input -->
            <div v-if="!historyLoading" class="chat-box" 
                 @dragover="handleDragOver" 
                 @drop="handleDrop"
                 @click="triggerFileUpload" 
                 :class="{ 'drag-over': isDragOver, 'disabled': loading }"
                 @dragenter="isDragOver = true"
                 @dragleave="isDragOver = false">
              <!-- File Upload Icon (Paperclip) -->
              <div class="file-upload-icon" @click.stop="triggerFileUpload" :class="{ 'disabled': loading }">
                <img :src="require('@/assets/Images/FDPal/paperclip-2.svg')" alt="Upload File" />
              </div>
              
              <v-textarea v-model="message" placeholder="Ask a question about fluids characterization data, or drag and drop your PVT pdf file..." rounded flat no-resize
                class="input-box" @click.stop></v-textarea>
              
              <!-- Send Icon (Paper Airplane) -->
              <div class="send-icon-wrapper" @click.stop="sendMessage">
                <v-progress-circular v-if="loading" indeterminate color="primary" size="20"></v-progress-circular>
                <img v-else :src="sendImg" alt="Send" />
              </div>
            </div>

            <!-- Hidden file input -->
            <input 
              type="file" 
              ref="fileInput" 
              @change="handleFileSelect" 
              accept=".pdf" 
              class="fd-intel-hidden-file"
            />

            <!-- Helper text when PDF is uploaded -->
            <div v-if="!historyLoading && fileName && falsePDF && !pdfContextUsed" class="pdf-context-helper">
              <small class="text-muted">
                <v-icon small color="info">info</v-icon>
                PDF file uploaded successfully. You can now ask questions about this report.
              </small>
            </div>

            <!-- Button Row -->
            <div v-if="!historyLoading" class="button-row">
              <!-- Buttons when no PDF is uploaded -->
              <template v-if="!isPDFUploaded">
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'summarize' }"
                       :disabled="loading"
                       @click="handlePromptClick('summarize', 'Summarize data from database for well: (add your well name here)')">
                  <img :src="require('@/assets/Images/FDPal/icons/summarize.svg')" alt="Summarize" />
                  Summarize data
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'samples' }"
                       :disabled="loading"
                       @click="handlePromptClick('samples', 'How many samples were collected from database for well: (add your well name). Provide me the details of the samples from this well.')">
                  <img :src="require('@/assets/Images/FDPal/icons/samples.svg')" alt="# of samples" />
                  No. of samples
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'api' }"
                       :disabled="loading"
                       @click="handlePromptClick('api', 'What is the API of the samples from database for well: (add your well name).')">
                  <img :src="require('@/assets/Images/FDPal/icons/api.svg')" alt="API" />
                  API
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'gor' }"
                       :disabled="loading"
                       @click="handlePromptClick('gor', 'What is the GOR of the samples from database for well: (add your well name).')">
                  <img :src="require('@/assets/Images/FDPal/icons/bubble-point.svg')" alt="GOR" />
                  GOR
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'bubble' }"
                       :disabled="loading"
                       @click="handlePromptClick('bubble', 'What is the bubble point pressure of the samples from database for well: (add your well name).')">
                  <img :src="require('@/assets/Images/FDPal/icons/bubble-point.svg')" alt="Bubble point" />
                  Bubble Point
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'dew' }"
                       :disabled="loading"
                       @click="handlePromptClick('dew', 'What is the dew point pressure of the samples from database for well: (add your well name).')">
                  <img :src="require('@/assets/Images/FDPal/icons/bubble-point.svg')" alt="Dew point" />
                  Dew Point
                </v-btn>
              </template>
              
              <!-- Buttons when PDF is uploaded -->
              <template v-else>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'summarize' }"
                       :disabled="loading"
                       @click="handlePromptClick('summarize', 'Summarize this PVT report.')">
                  <img :src="require('@/assets/Images/FDPal/icons/summarize.svg')" alt="Summarize" />
                  Summarize Report
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'samples' }"
                       :disabled="loading"
                       @click="handlePromptClick('samples', 'How many samples were collected for this study? Provide me the details of the samples in this study.')">
                  <img :src="require('@/assets/Images/FDPal/icons/samples.svg')" alt="# of samples" />
                  No. of samples
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'api' }"
                       :disabled="loading"
                       @click="handlePromptClick('api', 'What is the API of the fluid in this report?')">
                  <img :src="require('@/assets/Images/FDPal/icons/api.svg')" alt="API" />
                  API
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'gor' }"
                       :disabled="loading"
                       @click="handlePromptClick('gor', 'What is the GOR of the samples in this PVT study?')">
                  <img :src="require('@/assets/Images/FDPal/icons/bubble-point.svg')" alt="GOR" />
                  GOR
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'bubble' }"
                       :disabled="loading"
                       @click="handlePromptClick('bubble', 'What is the bubble point pressure of the samples in this PVT study?')">
                  <img :src="require('@/assets/Images/FDPal/icons/bubble-point.svg')" alt="Bubble point" />
                  Bubble point
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'dew' }"
                       :disabled="loading"
                       @click="handlePromptClick('dew', 'What is the dew point pressure of the samples in this PVT study?')">
                  <img :src="require('@/assets/Images/FDPal/icons/bubble-point.svg')" alt="Dew point" />
                  Dew point
                </v-btn>
              </template>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
  <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="snackbarTimeout" :top="snackbarTop">
    {{ snackbarText }}
    <template v-slot:action="{ attrs }">
      <v-btn text v-bind="attrs" @click="snackbar = false">
        Close
      </v-btn>
    </template>
  </v-snackbar>
</template>

<script>
import axios from 'axios';
import MarkdownIt from 'markdown-it';
import { mapGetters } from 'vuex';
import StudioData from '../layout/FluidsDataStudioTwo/StudioData.vue';
const md = new MarkdownIt({
  html: false,
  linkify: true,
  typographer: true
});

export default {
  components: {
    StudioData
  },
  props: {
    uploadedFile: {
      type: File,
      default: null,
    },
  },
  data() {
    return {
      selectedModel: 'gpt-4o',
      models: [
        {
          name: 'gpt-4o',
          company: 'OpenAI',
          icon: require('../../assets/Images/FDPal/models/gpt4o.svg'),
        },
        {
          name: 'Llama-3.3-70B-Instruct',
          company: 'Meta',
          icon: require('../../assets/Images/FDPal/models/llama3.svg'),
        },
        {
          name: 'Phi-4',
          company: 'Microsoft',
          icon: require('../../assets/Images/FDPal/models/phi4.svg'),
        },
        {
          name: 'Meta-Llama-3.1-405B-Instruct',
          company: 'Meta',
          icon: require('../../assets/Images/FDPal/models/llama3.svg'),
        },
        {
          name: 'DeepSeek-R1',
          company: 'DeepSeek',
          icon: require('../../assets/Images/FDPal/models/deepseek.svg'),
        },
      ],
      message: '',
      sendImg: require("../../assets/Images/FDPal/send-2.svg"),
      likeImg: require("../../assets/Images/FDPal/like.svg"),
      dislikeImg: require("../../assets/Images/FDPal/dislike.svg"),
      aiLogo: require("../../assets/Images/FDPal/aiLogo.png"),
      chatContext: '',
      history: [],
      loading: false,
      historyLoading: false, // Separate loading state for history
      snackbar: false,
      snackbarText: 'Ready to help with your fluids analysis questions!',
      snackbarColor: 'error',
      snackbarTimeout: 6000,
      snackbarTop: true,
      windowHeight: window.innerHeight,
      dislikeIndex: null,
      feedbackText: '',
      getFirstMsg: true,
      falsePDF: false,
      isDragOver: false,
      localUploadedFile: null, // Local file reference
      fileName: null, // Store the uploaded file name
      likeIndex: null, // Track the index of the like button
      activePromptButton: null, // Track the active prompt button
      pdfContextUsed: false, // Track if PDF context has been used for first message
      isUploading: false, // Flag to prevent duplicate uploads
      originalScrollTop: 0, // Store original scroll position
      justSentMessage: false, // Flag to track if message was just sent
      scrollPreventionTimeout: null, // Timeout for scroll prevention
      preventScroll: false, // Flag to prevent scrolling
      lastScrollPosition: 0, // Store last scroll position
      isInitialLoad: true, // Flag to track initial page load
      firstClickHandled: false, // Flag to track if first click has been handled
      firstClickPrevented: false, // Flag to permanently prevent first click scroll
      preventFirstScroll: false,
      scrollPreventionActive: false, // Flag to track if scroll prevention is active
      studioInteractionActive: false, // Flag to track if user is interacting with embedded studio
      studioInteractionTimeout: null, // Timeout for studio interaction flag
      firstClickScrollPreventionHandler: null, // Reference to scroll prevention event handler
      firstClickScrollPreventionInterval: null, // Reference to scroll prevention interval
      firstClickScrollPreventionRAF: null, // Reference to scroll prevention requestAnimationFrame
      isScrollbarInteraction: false, // Flag to track if user is interacting with scrollbar
      scrollbarInteractionTimeout: null, // Timeout to clear scrollbar interaction flag
      autoResetScrollPreventionTimeout: null, // Timeout to automatically reset scroll prevention
      isWheelScrolling: false, // Flag to track if scroll is from mouse wheel
      wheelScrollTimeout: null, // Timeout to clear wheel scroll flag
    };
  },
  computed: {
    ...mapGetters("tenantId", ["getUser", "getSelectedTenant"]),
    tenantId() {
      const pathSegments = this.$route.path.split('/').filter(Boolean);
      return pathSegments[0] || '';
    },
    llmUrl() {
      const tenant = this.getSelectedTenant;
      const currentTenantId = tenant?.ID || this.tenantId;
      if (currentTenantId === "67f132db0de754aadd3e9552") {
        return "https://adnoc-intelligence.fluidsdata.ai/";
      }
      return process.env.VUE_APP_LLM_URL;
    },
    userInitials() {
      const user = this.getUser;
      if (user && user.firstName && user.lastName) {
        return (
          (user.firstName[0] || '').toUpperCase() +
          (user.lastName[0] || '').toUpperCase()
        );
      }
      return '';
    },
    userName() {
      return this.getUser?.firstName || 'there';
    },
    chatCardStyle() {
      if (this.windowHeight > 900) {
        return {
          height: '85vh',
          maxHeight: '800px',
          minHeight: '400px',
        };
      } else if (this.windowHeight > 700) {
        return {
          height: '90vh',
          maxHeight: '90vh',
          minHeight: '350px',
        };
      } else {
        return {
          height: '75vh',
          maxHeight: '95vh',
          minHeight: '250px',
        };
      }
    },
    isPDFUploaded() {
      return this.fileName !== null && this.falsePDF;
    }
  },
  methods: {
    async handleFeedback(index, isPositive) {
      if (isPositive) {
        if (this.likeIndex === index) {
          this.likeIndex = null; // Deselect if already selected
        } else {
          this.likeIndex = index;
          this.dislikeIndex = null; // Deselect dislike if it was selected
        }
        return;
      }

      if (this.dislikeIndex === index) {
        this.dislikeIndex = null;
        this.feedbackText = '';
        return;
      } else {
        this.dislikeIndex = index;
        this.likeIndex = null; // Deselect like if it was selected
        this.feedbackText = '';
      }

      try {
        const response = await axios.post(
          `${this.llmUrl}feedback`,
          {
            message_index: index,
            is_positive: isPositive,
            feedback_text: isPositive ? null : this.feedbackText
          },
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem("authToken")}`,
              'X-TenantID': this.tenantId
            }
          }
        );

        if (response.data.status === 401) {
          this.$router.push('/login');
          return;
        }

        if (isPositive) {
          this.snackbarText = 'Thank you for your positive feedback!';
          this.snackbarColor = 'success';
        } else {
          this.snackbarText = 'Thank you for your feedback!';
          this.snackbarColor = 'success';
        }

        this.snackbar = true;
      } catch (error) {
        console.error("Feedback error:", error);
      } finally {
        if (isPositive) {
          this.dislikeIndex = null;
          this.feedbackText = '';
        }
      }
    },

    async submitFeedback(index) {
      try {
        const response = await axios.post(
          `${this.llmUrl}feedback`,
          {
            message_index: index,
            is_positive: false,
            feedback_text: this.feedbackText
          },
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem("authToken")}`,
              'X-TenantID': this.tenantId
            }
          }
        );

        if (response.data.status === 401) {
          this.$router.push('/login');
          return;
        }

        this.snackbarText = 'Thank you for your feedback!';
        this.snackbarColor = 'success';
        this.snackbar = true;

        this.dislikeIndex = null;
        this.feedbackText = '';

        await this.getHistory();

      } catch (error) {
        console.error("Feedback error:", error);
        this.snackbarText = 'Error submitting feedback';
        this.snackbarColor = 'error';
        this.snackbar = true;
      }
    },
    toggleDislike(index) {
      if (this.dislikeIndex === index) {
        this.dislikeIndex = null;
      } else {
        this.dislikeIndex = index;
        this.feedbackText = '';
      }
    },
    renderMarkdown(text) {
      return md.render(text || '');
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.chatHistory;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    },
    forceScrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.chatHistory;
        if (container) {
          const wasPreventing = this.preventScroll;
          this.preventScroll = false;
          if (this.preventScrollSetter) {
            this.preventScrollSetter(false);
          }
          
          container.scrollTop = container.scrollHeight;
          this.lastScrollPosition = container.scrollHeight;
          
          if (!this.isInitialLoad && wasPreventing) {
            setTimeout(() => {
              this.preventScroll = wasPreventing;
              if (this.preventScrollSetter) {
                this.preventScrollSetter(wasPreventing);
              }
            }, 100);
          }
        }
      });
    },
    async uploadPdf() {
      if (this.isUploading) {
        return; // Prevent duplicate uploads
      }
      this.isUploading = true;
      this.loading = true; // Start loading for the upload process
      const formData = new FormData();
      this.falsePDF = false;
      formData.append('file_upload', this.localUploadedFile);

      this.snackbarText = 'Upload in progress...';
      this.snackbarColor = 'info';
      this.snackbar = true;

      try {
        const response = await axios.post(`${this.llmUrl}upload-pdf`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${localStorage.getItem("authToken")}`,
            'X-TenantID': this.tenantId
          }
        });

        if (response.data.status === 401) {
          this.snackbarText = 'Session expired. Please login again.';
          this.snackbarColor = 'error';
          this.snackbar = true;
          this.$router.push('/login');
          return;
        }

        let uploadedFileName = null;
        if (response.data) {
          uploadedFileName = response.data.response || 
                            response.data.filename || 
                            response.data.file_name || 
                            response.data.fileName ||
                            response.data.name;
        }
        
        if (!uploadedFileName && this.localUploadedFile && this.localUploadedFile.name) {
          uploadedFileName = this.localUploadedFile.name;
        }
        
        if (!uploadedFileName) {
          console.warn('Filename not found in response, using default');
          uploadedFileName = 'uploaded_file.pdf';
        }
        
        this.fileName = uploadedFileName;
        this.falsePDF = true;
        this.pdfContextUsed = false; // Reset flag for new PDF
        
        const pdfMessage = `Summarize this PVT report pdf: ${this.fileName}`;
        this.message = pdfMessage;
        
        this.snackbarText = 'File uploaded successfully!';
        this.snackbarColor = 'success';
        this.snackbar = true;
        
        this.loading = false; // Reset loading since we're not auto-sending

      } catch (error) {
        this.falsePDF = false;
        console.error("Upload error:", error);
        
        if (error.response) {
          this.snackbarText = error.response.data.message || 'Error uploading file';
        } else if (error.request) {
          this.snackbarText = 'No response from server. Please try again.';
        } else {
          this.snackbarText = 'Error uploading file. Please try again.';
        }
        this.snackbarColor = 'error';
        this.snackbar = true;
        this.loading = false; // Reset loading on error
      } finally {
        this.isUploading = false; // Reset upload flag
      }
    },
    async sendMessage() {
      if (this.loading || !this.message.trim()) return;
      if (this.message.trim()) {
        this.getFirstMsg = false;
        this.loading = true; // Start loading animation
        const userMsg = this.message;
        this.history.push({ 
          role: 'user', 
          context: userMsg,
          metadata: null // User messages don't have metadata
        });
        this.message = '';
        
        this.justSentMessage = true;
        
        const container = this.$refs.chatHistory;
        if (container) {
          if (this.firstClickScrollPreventionHandler) {
            container.removeEventListener('scroll', this.firstClickScrollPreventionHandler, { capture: true });
            this.firstClickScrollPreventionHandler = null;
          }
          if (this.firstClickWheelPreventionHandler) {
            container.removeEventListener('wheel', this.firstClickWheelPreventionHandler, { capture: true });
            this.firstClickWheelPreventionHandler = null;
          }
          if (this.firstClickScrollPreventionInterval) {
            clearInterval(this.firstClickScrollPreventionInterval);
            this.firstClickScrollPreventionInterval = null;
          }
          if (this.firstClickScrollPreventionRAF !== null) {
            cancelAnimationFrame(this.firstClickScrollPreventionRAF);
            this.firstClickScrollPreventionRAF = null;
          }
        }
        
        this.preventScroll = false;
        this.preventFirstScroll = false;
        if (this.preventScrollSetter) {
          this.preventScrollSetter(false);
        }
        
        this.forceScrollToBottom();
        
        this.$nextTick(() => {
          setTimeout(() => {
            const container = this.$refs.chatHistory;
            if (container) {
              this.lastScrollPosition = container.scrollTop;
              this.justSentMessage = false;
              
              console.log('🟡 Message sent - enabling scroll prevention to prevent initial scroll up');
              
              this.firstClickHandled = false; // Allow first-click handler to run if click happens
              this.preventFirstScroll = true; // Enable to prevent initial scroll up
              this.preventScroll = true; // Enable to prevent initial scroll up
              
              if (this.preventScrollSetter) {
                this.preventScrollSetter(true);
              }
              
              const currentScrollTop = container.scrollTop;
              this.lastScrollPosition = currentScrollTop;
              
              const initialScrollPrevention = (e) => {
                const newScrollTop = container.scrollTop;
                if (newScrollTop < currentScrollTop) {
                  console.log('🔴 Preventing initial scroll up - restoring to', currentScrollTop, 'from', newScrollTop);
                  e.preventDefault();
                  e.stopPropagation();
                  container.scrollTop = currentScrollTop;
                } else {
                  console.log('🟢 User scrolling down - allowing and clearing initial prevention');
                  container.removeEventListener('scroll', initialScrollPrevention, { capture: true });
                  this.preventFirstScroll = false;
                  this.preventScroll = false;
                  if (this.preventScrollSetter) {
                    this.preventScrollSetter(false);
                  }
                }
              };
              
              container.addEventListener('scroll', initialScrollPrevention, { passive: false, capture: true });
              
              setTimeout(() => {
                container.removeEventListener('scroll', initialScrollPrevention, { capture: true });
                this.preventFirstScroll = false;
                this.preventScroll = false;
                if (this.preventScrollSetter) {
                  this.preventScrollSetter(false);
                }
                console.log('🟢 Initial scroll prevention disabled - user can now scroll normally');
              }, 1000); // Disable after 1 second to allow normal scrolling
              
              console.log('🟢 Scroll prevention enabled - will prevent initial scroll up', {
                firstClickHandled: this.firstClickHandled,
                preventFirstScroll: this.preventFirstScroll,
                preventScroll: this.preventScroll,
                lastScrollPosition: this.lastScrollPosition
              });
            }
          }, 500); // Wait for scroll to complete
        });
        
        this.$nextTick(() => {
          this.firstClickHandled = false;
        });
        
        if (this.scrollPreventionTimeout) {
          clearTimeout(this.scrollPreventionTimeout);
        }
        
        this.scrollPreventionTimeout = setTimeout(() => {
          this.justSentMessage = false;
        }, 3000);

        if (this.localUploadedFile && this.fileName && !this.pdfContextUsed) {
          await this.askQuestion(userMsg);
          this.pdfContextUsed = true;
        } else {
          await this.askQuestion(userMsg);
        }
      }
    },
    async askQuestion(userMsg) {
      if (this.localUploadedFile && !this.falsePDF) {
        this.loading = false;
        return;
      }
      
      let questionText = userMsg;
      let questionData = {
        message: questionText
      };
      
      if (this.fileName && this.falsePDF && !this.pdfContextUsed) {
        questionData.source_pdf = this.fileName;
        questionData.message = userMsg;
      }
      
      try {
        const response = await axios.post(`${this.llmUrl}api/chat`, questionData, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem("authToken")}`,
            'X-TenantID': this.tenantId
          }
        });
        if (response.data.status === 401) {
          this.$router.push('/login');
          return;
        }
        const responseData = response.data.response;
        
        if (typeof responseData === 'object' && responseData.type) {
          const historyItem = { 
            role: 'ai', 
            context: responseData.content || '', // Content may be empty for plot type
            type: responseData.type,
            metadata: responseData.metadata ? { ...responseData.metadata } : {} // Include metadata if available
          };
          
          if (responseData.plotUrl) {
            historyItem.plotUrl = responseData.plotUrl;
            historyItem.plotLoading = true; // Initialize loading state
            historyItem.plotError = false;
          }
          
          if (responseData.xAxis) historyItem.xAxis = responseData.xAxis;
          if (responseData.yAxis) historyItem.yAxis = responseData.yAxis;
          if (responseData.legend) historyItem.legend = responseData.legend;
          
          if (responseData.route) {
            historyItem.route = responseData.route;
            if (!historyItem.metadata.route) {
              historyItem.metadata.route = responseData.route;
            }
          }
          
          if (!historyItem.metadata || typeof historyItem.metadata !== 'object') {
            historyItem.metadata = {};
          }
          
          this.chatContext = historyItem.context || (historyItem.plotUrl ? 'Plot visualization' : '');
          this.history.push(historyItem);
          
          if (historyItem.plotUrl) {
            const historyIndex = this.history.length - 1;
            setTimeout(() => {
              if (this.history[historyIndex] && this.history[historyIndex].plotLoading) {
                this.history[historyIndex].plotLoading = false;
              }
            }, 5000);
          }
        } else {
          this.chatContext = responseData;
          this.history.push({ 
            role: 'ai', 
            context: responseData,
            metadata: null // No metadata in old format
          });
        }
        this.$nextTick(() => {
          const container = this.$refs.chatHistory;
          if (container) {
            if (this.firstClickScrollPreventionHandler) {
              container.removeEventListener('scroll', this.firstClickScrollPreventionHandler, { capture: true });
              this.firstClickScrollPreventionHandler = null;
            }
            if (this.firstClickWheelPreventionHandler) {
              container.removeEventListener('wheel', this.firstClickWheelPreventionHandler, { capture: true });
              this.firstClickWheelPreventionHandler = null;
            }
            if (this.firstClickScrollPreventionInterval) {
              clearInterval(this.firstClickScrollPreventionInterval);
              this.firstClickScrollPreventionInterval = null;
            }
            if (this.firstClickScrollPreventionRAF !== null) {
              cancelAnimationFrame(this.firstClickScrollPreventionRAF);
              this.firstClickScrollPreventionRAF = null;
            }
          }
          
          this.preventScroll = false;
          this.preventFirstScroll = false;
          if (this.preventScrollSetter) {
            this.preventScrollSetter(false);
          }
          
          this.forceScrollToBottom();
          
          setTimeout(() => {
            const container = this.$refs.chatHistory;
            if (container) {
              this.lastScrollPosition = container.scrollTop;
              this.justSentMessage = false;
              
              console.log('🟡 AI response received - enabling scroll prevention to prevent initial scroll up');
              
              this.firstClickHandled = false; // Allow first-click handler to run if click happens
              this.preventFirstScroll = true; // Enable to prevent initial scroll up
              this.preventScroll = true; // Enable to prevent initial scroll up
              
              if (this.preventScrollSetter) {
                this.preventScrollSetter(true);
              }
              
              const currentScrollTop = container.scrollTop;
              this.lastScrollPosition = currentScrollTop;
              
              const initialScrollPrevention = (e) => {
                const newScrollTop = container.scrollTop;
                if (newScrollTop < currentScrollTop) {
                  console.log('🔴 Preventing initial scroll up after AI response - restoring to', currentScrollTop, 'from', newScrollTop);
                  e.preventDefault();
                  e.stopPropagation();
                  container.scrollTop = currentScrollTop;
                } else {
                  console.log('🟢 User scrolling down after AI response - allowing and clearing initial prevention');
                  container.removeEventListener('scroll', initialScrollPrevention, { capture: true });
                  this.preventFirstScroll = false;
                  this.preventScroll = false;
                  if (this.preventScrollSetter) {
                    this.preventScrollSetter(false);
                  }
                }
              };
              
              container.addEventListener('scroll', initialScrollPrevention, { passive: false, capture: true });
              
              setTimeout(() => {
                container.removeEventListener('scroll', initialScrollPrevention, { capture: true });
                this.preventFirstScroll = false;
                this.preventScroll = false;
                if (this.preventScrollSetter) {
                  this.preventScrollSetter(false);
                }
                console.log('🟢 Initial scroll prevention disabled after AI response - user can now scroll normally');
              }, 1000); // Disable after 1 second to allow normal scrolling
              
              console.log('🟢 Scroll prevention enabled after AI response - will prevent initial scroll up', {
                firstClickHandled: this.firstClickHandled,
                preventFirstScroll: this.preventFirstScroll,
                preventScroll: this.preventScroll,
                lastScrollPosition: this.lastScrollPosition
              });
            }
          }, 500); // Wait for scroll to complete
        });
        
        this.$nextTick(() => {
          this.firstClickHandled = false;
        });
      } catch (error) {
        console.error("Answer Question error:", error);
        this.snackbarText = 'Error getting response. Please try again.';
        this.snackbarColor = 'error';
        this.snackbar = true;
      } finally {
        this.loading = false; // Stop loading animation
      }
    },
    async getHistory() {
      this.historyLoading = true; // Start history loading animation
      this.preventScroll = false;
      this.justSentMessage = true; // Temporarily set to prevent scroll interference
      if (this.preventScrollSetter) {
        this.preventScrollSetter(false);
      }
      try {
        const response = await axios.post(`${this.llmUrl}get-history`, {}, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem("authToken")}`,
            'X-TenantID': this.tenantId
          }
        });
        
        
        if (response.data.status === 401 || response.data.statusCode === 401) {
          this.$router.push('/login');
          return;
        }
        
        let historyData = [];
        
        if (response.data.data && response.data.data.response && Array.isArray(response.data.data.response)) {
          historyData = response.data.data.response;
        }
        else if (response.data.history && Array.isArray(response.data.history)) {
          historyData = response.data.history;
        } 
        else if (response.data.response && response.data.response.history && Array.isArray(response.data.response.history)) {
          historyData = response.data.response.history;
        }
        else if (response.data.response && Array.isArray(response.data.response)) {
          if (response.data.response[0] && response.data.response[0].content && typeof response.data.response[0].content === 'object') {
            historyData = response.data.response;
          } else {
            const lastUserMessage = response.data.response
              .filter(item => item.role === "user")
              .slice(-1)[0];
            this.history = response.data.response.map(item => {
              let content = item.content;
              let type = item.type || 'text'; // Default to text type
              
              if (item.role !== "ai") {
                const regex = /Question:\s*([^]*)/;
                const match = content.match(regex);
                content = match ? match[1] : content;

                if (lastUserMessage) {
                  const pdfRegex = /pdf\s*:\s*(.+?\.pdf)/i;
                  const pdfMatch = content.match(pdfRegex);
                  if (pdfMatch) {
                    this.fileName = pdfMatch[1];
                  }
                }
              }
              return {
                role: item.role,
                context: content,
                type: type,
                metadata: item.metadata || null // Include metadata if available in old format
              };
            });
            if (this.history.length > 0) {
              this.getFirstMsg = false;
              this.$nextTick(() => {
                this.forceScrollToBottom();
              });
            } else {
              void 0;
            }
            return; // Exit early for old format
          }
        } else {
          this.history = [];
          return;
        }
        
        this.history = [];
        let lastUserMessage = null;
        
        const hasHistoryData = historyData.length > 0;
        
        historyData.forEach(item => {
          const content = item.content || {};
          const userMessage = content.user || '';
          const aiMessage = content.ai || '';
          const mimeType = item.mime_type || 'MemoryMimeType.TEXT';
          const metadata = item.metadata || {}; // Store metadata
          
          let type = 'text';
          let plotUrl = null;
          let xAxis = null;
          let yAxis = null;
          let legend = null;
          
          if (typeof aiMessage === 'object' && aiMessage !== null) {
            type = aiMessage.type || 'text';
            plotUrl = aiMessage.plotUrl || null;
            xAxis = aiMessage.xAxis || null;
            yAxis = aiMessage.yAxis || null;
            legend = aiMessage.legend || null;
          } else if (mimeType.includes('HTML') || mimeType.includes('html')) {
            type = 'html';
          }
          
          if (userMessage.trim()) {
            let processedUserMessage = userMessage;
            
            const pdfRegex = /pdf\s*:\s*(.+?\.pdf)/i;
            const pdfMatch = processedUserMessage.match(pdfRegex);
            if (pdfMatch) {
              this.fileName = pdfMatch[1];
            }
            
            const regex = /Question:\s*([^]*)/;
            const match = processedUserMessage.match(regex);
            processedUserMessage = match ? match[1] : processedUserMessage;
            
            this.history.push({
              role: 'user',
              context: processedUserMessage,
              type: 'text',
              metadata: null // User messages don't have metadata
            });
            
            lastUserMessage = processedUserMessage;
          }
          
          if (aiMessage && (typeof aiMessage === 'string' ? aiMessage.trim() : true)) {
            const historyItem = {
              role: 'ai',
              context: typeof aiMessage === 'string' ? aiMessage : (aiMessage.content || ''),
              type: type,
              metadata: metadata // Store metadata with AI message
            };
            
            if (plotUrl) {
              historyItem.plotUrl = plotUrl;
              historyItem.plotLoading = true; // Initialize loading state
              historyItem.plotError = false;
            }
            if (xAxis) {
              historyItem.xAxis = xAxis;
            }
            if (yAxis) {
              historyItem.yAxis = yAxis;
            }
            if (legend) {
              historyItem.legend = legend;
            }
            
            if (historyItem.context || historyItem.plotUrl) {
              this.history.push(historyItem);
            }
          }
        });
        
        if (hasHistoryData || this.history.length > 0) {
          this.getFirstMsg = false;
          this.$nextTick(() => {
            this.forceScrollToBottom();
            setTimeout(() => {
              const container = this.$refs.chatHistory;
              if (container) {
                this.lastScrollPosition = container.scrollTop;
              }
            }, 200);
          });
        } else {
          void 0;
        }
      } catch (error) {
        console.error("Get History error:", error);
        this.snackbarText = 'Error loading chat history';
        this.snackbarColor = 'error';
        this.snackbar = true;
      } finally {
        this.historyLoading = false; // Stop history loading animation
        this.$nextTick(() => {
          const container = this.$refs.chatHistory;
          if (container) {
            this.lastScrollPosition = container.scrollTop;
          }
          
          setTimeout(() => {
            this.justSentMessage = false;
            if (container) {
              this.lastScrollPosition = container.scrollTop;
            }
            this.isInitialLoad = false;
            this.clearScrollPreventionLocks();
            this.setupScrollPrevention();
          }, 600);
        });
      }
    },
    handleResize() {
      this.windowHeight = window.innerHeight;
    },
    handleDragOver(event) {
      if (this.loading) return; // Prevent drag and drop during loading
      event.preventDefault();
      event.stopPropagation();
      event.dataTransfer.dropEffect = 'copy';
    },
    handleDrop(event) {
      if (this.loading) return; // Prevent file drop during loading
      event.preventDefault();
      event.stopPropagation();
      this.isDragOver = false; // Reset drag-over state
      const file = event.dataTransfer.files[0];
      if (file && file.type === 'application/pdf' && !this.isUploading) {
        this.localUploadedFile = file;
        this.$emit('file-uploaded', file); // Emit to parent
        this.uploadPdf(); // Upload the PDF
      } else if (this.isUploading) {
        this.snackbarText = 'File upload already in progress. Please wait.';
        this.snackbarColor = 'warning';
        this.snackbar = true;
      } else {
        this.snackbarText = 'Please drop a PDF file.';
        this.snackbarColor = 'warning';
        this.snackbar = true;
      }
    },
    handleFileSelect(event) {
      if (this.loading) return; // Prevent file selection during loading
      const file = event.target.files[0];
      if (file && file.type === 'application/pdf' && !this.isUploading) {
        this.localUploadedFile = file;
        this.$emit('file-uploaded', file); // Emit to parent
        this.uploadPdf(); // Upload the PDF
      } else if (this.isUploading) {
        this.snackbarText = 'File upload already in progress. Please wait.';
        this.snackbarColor = 'warning';
        this.snackbar = true;
      } else {
        this.snackbarText = 'Please select a PDF file.';
        this.snackbarColor = 'warning';
        this.snackbar = true;
      }
      event.target.value = ''; // Clear the input
    },
    triggerFileUpload() {
      if (this.loading) return; // Prevent file upload during loading
      if (this.$refs.innerFileInput) {
        this.$refs.innerFileInput.click();
      } else if (this.$refs.fileInput) {
        this.$refs.fileInput.click();
      }
    },
    closeFeedback() {
      this.dislikeIndex = null;
      this.feedbackText = '';
    },
    handlePromptClick(type, text) {
      this.activePromptButton = type;
      
      if (this.fileName && this.falsePDF) {
        this.message = `${text} pdf: ${this.fileName}`;
      } else {
        this.message = text;
      }
    },
    clearPDFState() {
      this.localUploadedFile = null;
      this.fileName = null;
      this.falsePDF = false;
      this.pdfContextUsed = false;
      this.message = '';
      this.activePromptButton = null;
    },
    clearScrollPreventionLocks() {
      this.preventFirstScroll = false;
      this.preventScroll = false;
      if (this.preventScrollSetter) {
        this.preventScrollSetter(false);
      }
      const container = this.$refs.chatHistory;
      if (this.firstClickScrollPreventionInterval) {
        clearInterval(this.firstClickScrollPreventionInterval);
        this.firstClickScrollPreventionInterval = null;
      }
      if (this.firstClickScrollPreventionRAF !== null) {
        cancelAnimationFrame(this.firstClickScrollPreventionRAF);
        this.firstClickScrollPreventionRAF = null;
      }
      if (this.firstClickScrollPreventionHandler && container) {
        container.removeEventListener('scroll', this.firstClickScrollPreventionHandler, { capture: true });
        this.firstClickScrollPreventionHandler = null;
      }
      if (this.firstClickWheelPreventionHandler && container) {
        container.removeEventListener('wheel', this.firstClickWheelPreventionHandler, { capture: true });
        this.firstClickWheelPreventionHandler = null;
      }
    },
    setupScrollPrevention() {
      this.$nextTick(() => {
        const container = this.$refs.chatHistory;
        if (container) {
          this.originalScrollTop = container.scrollTop;
          
          this.preventScrollSetter = (prevent) => {
            void 0;
          };
        }
      });
    },
    handleDocumentClick(event) {
      const container = this.$refs.chatHistory;
      if (!container) return;
      
      const target = event.target;
      const isClickInChatHistory = container.contains(target) || container === target;
      
      if (isClickInChatHistory) {
        const rect = container.getBoundingClientRect();
        const scrollbarWidth = container.offsetWidth - container.clientWidth;
        const clickX = event.clientX - rect.left;
        const isScrollbarClick = clickX > container.clientWidth - scrollbarWidth - 5; // Add 5px buffer for better detection
        
        if (isScrollbarClick) {
          console.log('🔵 SCROLLBAR CLICK DETECTED in handleDocumentClick - disabling all scroll prevention');
          
          this.isScrollbarInteraction = true;
          this.preventFirstScroll = false;
          this.preventScroll = false;
          this.studioInteractionActive = false;
          
          if (this.firstClickScrollPreventionInterval) {
            clearInterval(this.firstClickScrollPreventionInterval);
            this.firstClickScrollPreventionInterval = null;
          }
          if (this.firstClickScrollPreventionRAF !== null) {
            cancelAnimationFrame(this.firstClickScrollPreventionRAF);
            this.firstClickScrollPreventionRAF = null;
          }
          if (this.firstClickScrollPreventionHandler) {
            container.removeEventListener('scroll', this.firstClickScrollPreventionHandler, { capture: true });
            this.firstClickScrollPreventionHandler = null;
          }
          
          if (this.scrollbarInteractionTimeout) {
            clearTimeout(this.scrollbarInteractionTimeout);
          }
          this.scrollbarInteractionTimeout = setTimeout(() => {
            this.isScrollbarInteraction = false;
          }, 3000);
          
          return; // Allow the click to proceed normally - don't block scrollbar
        }
        
        const isClickOnEmbeddedStudio = target.closest('.embedded-studio-container') || 
                                       target.closest('.embedded-studio-content') ||
                                       target.closest('.embedded-studio-footer');
        
        if (isClickOnEmbeddedStudio) {
         
          
          this.preventFirstScroll = false;
          this.studioInteractionActive = true;
          this.preventScroll = false;
          
          if (this.firstClickScrollPreventionInterval) {
            clearInterval(this.firstClickScrollPreventionInterval);
            this.firstClickScrollPreventionInterval = null;
          }
          if (this.firstClickScrollPreventionRAF !== null) {
            cancelAnimationFrame(this.firstClickScrollPreventionRAF);
            this.firstClickScrollPreventionRAF = null;
          }
          if (this.firstClickScrollPreventionHandler) {
            const container = this.$refs.chatHistory;
            if (container) {
              container.removeEventListener('scroll', this.firstClickScrollPreventionHandler, { capture: true });
            }
            this.firstClickScrollPreventionHandler = null;
          }
          if (this.firstClickWheelPreventionHandler) {
            const container = this.$refs.chatHistory;
            if (container) {
              container.removeEventListener('wheel', this.firstClickWheelPreventionHandler, { capture: true });
            }
            this.firstClickWheelPreventionHandler = null;
          }
          
          this.disableScrollPreventionForStudio();
          
          return; // Allow the click to proceed normally
        }
        
        if (!this.firstClickHandled && !this.isScrollbarInteraction) {
          console.log('🔵 First click detected on empty space - setting up scroll prevention', {
            firstClickHandled: this.firstClickHandled,
            preventFirstScroll: this.preventFirstScroll
          });
          this.firstClickHandled = true;
          
          const container = this.$refs.chatHistory;
          if (container) {
            const currentScrollTop = container.scrollTop;
            this.lastScrollPosition = currentScrollTop;
            
            container.scrollTop = currentScrollTop;
            
            this.preventFirstScroll = true;
            this.preventScroll = true;
            
            if (this.preventScrollSetter) {
              this.preventScrollSetter(true);
            }
            
            this.firstClickScrollPreventionHandler = null;
            this.firstClickScrollPreventionInterval = null;
            this.firstClickScrollPreventionRAF = null;
            this.firstClickWheelPreventionHandler = null;
            
            const preventScrollHandler = (e) => {
              const activeElement = document.activeElement;
              const isInEmbeddedStudio = activeElement && (
                activeElement.closest('.embedded-studio-container') ||
                activeElement.closest('.embedded-studio-content')
              );
              
              const scrollTarget = e.target;
              const isScrollOnEmbeddedStudio = scrollTarget && (
                scrollTarget.closest('.embedded-studio-container') ||
                scrollTarget.closest('.embedded-studio-content')
              );
              
              const hasEmbeddedStudio = container.querySelector('.embedded-studio-container');
              
              const mouseEvent = e;
              let isMouseOverEmbeddedStudio = false;
              if (mouseEvent && mouseEvent.clientX && mouseEvent.clientY) {
                const elementAtPoint = document.elementFromPoint(mouseEvent.clientX, mouseEvent.clientY);
                isMouseOverEmbeddedStudio = elementAtPoint && (
                  elementAtPoint.closest('.embedded-studio-container') ||
                  elementAtPoint.closest('.embedded-studio-content')
                );
              }
              
              if (this.studioInteractionActive || isInEmbeddedStudio || isScrollOnEmbeddedStudio || hasEmbeddedStudio || isMouseOverEmbeddedStudio) {
                this.preventFirstScroll = false;
                this.studioInteractionActive = true;
                container.removeEventListener('scroll', preventScrollHandler, { capture: true });
                this.firstClickScrollPreventionHandler = null;
                return; // Allow scrolling during studio interaction
              }
              
              if (this.isScrollbarInteraction) {
                return;
              }
              
              if (this.preventFirstScroll && container.scrollTop !== currentScrollTop) {
                console.log('🔴 BLOCKING scroll on first click - restoring to', currentScrollTop, 'from', container.scrollTop);
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
                container.scrollTop = currentScrollTop;
                return false;
              }
            };
            
            this.firstClickScrollPreventionHandler = preventScrollHandler;
            
            container.addEventListener('scroll', preventScrollHandler, { passive: false, capture: true });
            
            const preventWheelHandler = (e) => {
              if (this.preventFirstScroll && !this.studioInteractionActive) {
                const activeElement = document.activeElement;
                const isInEmbeddedStudio = activeElement && (
                  activeElement.closest('.embedded-studio-container') ||
                  activeElement.closest('.embedded-studio-content')
                );
                
                if (!isInEmbeddedStudio) {
                  console.log('🔴 BLOCKING wheel scroll on first click');
                  e.preventDefault();
                  e.stopPropagation();
                  e.stopImmediatePropagation();
                  return false;
                }
              }
            };
            this.firstClickWheelPreventionHandler = preventWheelHandler;
            container.addEventListener('wheel', preventWheelHandler, { passive: false, capture: true });
            
            const restoreScrollLoop = () => {
              const activeElement = document.activeElement;
              const isInEmbeddedStudio = activeElement && (
                activeElement.closest('.embedded-studio-container') ||
                activeElement.closest('.embedded-studio-content')
              );
              
              const hasEmbeddedStudio = container.querySelector('.embedded-studio-container');
              
              if (this.studioInteractionActive || isInEmbeddedStudio || hasEmbeddedStudio) {
                this.preventFirstScroll = false;
                this.studioInteractionActive = true;
                this.firstClickScrollPreventionRAF = null;
                return; // Stop the loop if studio interaction is active
              }
              
              if (this.isScrollbarInteraction) {
                return;
              }
              
              if (this.preventFirstScroll && container.scrollTop !== currentScrollTop) {
                container.scrollTop = currentScrollTop;
              }
              
              if (this.preventFirstScroll && !this.studioInteractionActive && !isInEmbeddedStudio && !hasEmbeddedStudio) {
                this.firstClickScrollPreventionRAF = requestAnimationFrame(restoreScrollLoop);
              } else {
                this.firstClickScrollPreventionRAF = null;
              }
            };
            this.firstClickScrollPreventionRAF = requestAnimationFrame(restoreScrollLoop);
            
            const scrollPreventionInterval = setInterval(() => {
              const activeElement = document.activeElement;
              const isInEmbeddedStudio = activeElement && (
                activeElement.closest('.embedded-studio-container') ||
                activeElement.closest('.embedded-studio-content')
              );
              
              const hasEmbeddedStudio = container.querySelector('.embedded-studio-container');
              
              if (this.studioInteractionActive || isInEmbeddedStudio || hasEmbeddedStudio) {
                this.preventFirstScroll = false;
                this.studioInteractionActive = true;
                clearInterval(scrollPreventionInterval);
                this.firstClickScrollPreventionInterval = null;
                return; // Stop preventing if studio interaction is active
              }
              
              if (this.isScrollbarInteraction) {
                return;
              }
              
              if (this.preventFirstScroll && container.scrollTop !== currentScrollTop) {
                container.scrollTop = currentScrollTop;
              }
            }, 1); // Check every 1ms for maximum responsiveness
            
            this.firstClickScrollPreventionInterval = scrollPreventionInterval;
            
            setTimeout(() => {
              if (this.firstClickScrollPreventionInterval) {
                clearInterval(this.firstClickScrollPreventionInterval);
                this.firstClickScrollPreventionInterval = null;
              }
              if (this.firstClickScrollPreventionRAF !== null) {
                cancelAnimationFrame(this.firstClickScrollPreventionRAF);
                this.firstClickScrollPreventionRAF = null;
              }
              if (this.firstClickScrollPreventionHandler) {
                container.removeEventListener('scroll', this.firstClickScrollPreventionHandler, { capture: true });
                this.firstClickScrollPreventionHandler = null;
              }
              if (this.firstClickWheelPreventionHandler) {
                container.removeEventListener('wheel', this.firstClickWheelPreventionHandler, { capture: true });
                this.firstClickWheelPreventionHandler = null;
              }
              
              setTimeout(() => {
                this.preventFirstScroll = false;
                this.preventScroll = false;
                if (this.preventScrollSetter) {
                  this.preventScrollSetter(false);
                }
                console.log('🟢 Scroll prevention disabled after first click - normal scrolling now allowed');
              }, 500); // Increased delay to catch more delayed scrolls
            }, 800); // Increased delay before cleanup
            
          }
        }
        
        const isClickOnMessage = target.closest('.chat-bubble') || 
                                 target.closest('.v-list-item') ||
                                 target.closest('.v-list-item-content') ||
                                 target.closest('.studio-plot-link') ||
                                 target.closest('.visualization-prompt') ||
                                 target.closest('.html-content') ||
                                 target.closest('.embedded-studio-container') ||
                                 target.closest('.embedded-studio-content') ||
                                 target.closest('.embedded-studio-footer') ||
                                 target.closest('a') ||
                                 target.closest('button') ||
                                 target.closest('img') ||
                                 target.closest('input') ||
                                 target.closest('select') ||
                                 target.closest('textarea') ||
                                 target.closest('.v-select') ||
                                 target.closest('.v-text-field') ||
                                 target.closest('.v-btn');
        
        if (!isClickOnMessage) {
          const rect = container.getBoundingClientRect();
          const scrollbarWidth = container.offsetWidth - container.clientWidth;
          const clickX = event.clientX - rect.left;
          const isScrollbarClick = clickX > container.clientWidth - scrollbarWidth;
          
          if (!isScrollbarClick) {
            const isDirectContainerClick = target === container || 
                                           (target.classList && target.classList.contains('chat-history')) ||
                                           (target.classList && target.classList.contains('v-list') && !target.closest('.v-list-item'));
            
            if (isDirectContainerClick) {
              event.preventDefault();
              event.stopPropagation();
              event.stopImmediatePropagation();
              return false;
            }
          }
        }
      }
    },
    handleDocumentMouseDown(event) {
      const container = this.$refs.chatHistory;
      if (!container) return;
      
      const target = event.target;
      const isClickInChatHistory = container.contains(target) || container === target;
      
      if (isClickInChatHistory) {
        const rect = container.getBoundingClientRect();
        const scrollbarWidth = container.offsetWidth - container.clientWidth;
        const clickX = event.clientX - rect.left;
        const isScrollbarClick = clickX > container.clientWidth - scrollbarWidth - 5; // Add 5px buffer for better detection
        
        if (isScrollbarClick) {
          console.log('🔵 SCROLLBAR MOUSEDOWN DETECTED in handleDocumentMouseDown - disabling all scroll prevention');
          
          this.isScrollbarInteraction = true;
          this.preventFirstScroll = false;
          this.preventScroll = false;
          this.studioInteractionActive = false;
          
          if (this.firstClickScrollPreventionInterval) {
            clearInterval(this.firstClickScrollPreventionInterval);
            this.firstClickScrollPreventionInterval = null;
          }
          if (this.firstClickScrollPreventionRAF !== null) {
            cancelAnimationFrame(this.firstClickScrollPreventionRAF);
            this.firstClickScrollPreventionRAF = null;
          }
          if (this.firstClickScrollPreventionHandler) {
            container.removeEventListener('scroll', this.firstClickScrollPreventionHandler, { capture: true });
            this.firstClickScrollPreventionHandler = null;
          }
          
          if (this.scrollbarInteractionTimeout) {
            clearTimeout(this.scrollbarInteractionTimeout);
          }
          this.scrollbarInteractionTimeout = setTimeout(() => {
            this.isScrollbarInteraction = false;
          }, 3000);
          
          return; // Allow the mousedown to proceed normally - don't block scrollbar
        }
        
        const isClickOnEmbeddedStudio = target.closest('.embedded-studio-container') || 
                                       target.closest('.embedded-studio-content') ||
                                       target.closest('.embedded-studio-footer');
        
        if (isClickOnEmbeddedStudio) {
         
          
          this.preventFirstScroll = false;
          this.studioInteractionActive = true;
          this.preventScroll = false;
          
          if (this.firstClickScrollPreventionInterval) {
            clearInterval(this.firstClickScrollPreventionInterval);
            this.firstClickScrollPreventionInterval = null;
          }
          if (this.firstClickScrollPreventionRAF !== null) {
            cancelAnimationFrame(this.firstClickScrollPreventionRAF);
            this.firstClickScrollPreventionRAF = null;
          }
          if (this.firstClickScrollPreventionHandler) {
            if (container) {
              container.removeEventListener('scroll', this.firstClickScrollPreventionHandler, { capture: true });
            }
            this.firstClickScrollPreventionHandler = null;
          }
          if (this.firstClickWheelPreventionHandler) {
            if (container) {
              container.removeEventListener('wheel', this.firstClickWheelPreventionHandler, { capture: true });
            }
            this.firstClickWheelPreventionHandler = null;
          }
          
          this.disableScrollPreventionForStudio();
          
          return; // Allow the mousedown to proceed normally
        }
        
        if (!this.firstClickHandled && !this.isScrollbarInteraction) {
          const isClickOnMessage = target.closest('.chat-bubble') ||
                                   target.closest('.v-list-item') ||
                                   target.closest('.v-list-item-content') ||
                                   target.closest('.studio-plot-link') ||
                                   target.closest('.visualization-prompt') ||
                                   target.closest('.html-content') ||
                                   target.closest('.embedded-studio-container') ||
                                   target.closest('.embedded-studio-content') ||
                                   target.closest('.embedded-studio-footer') ||
                                   target.closest('a') ||
                                   target.closest('button') ||
                                   target.closest('img') ||
                                   target.closest('input') ||
                                   target.closest('select') ||
                                   target.closest('textarea') ||
                                   target.closest('.v-select') ||
                                   target.closest('.v-text-field') ||
                                   target.closest('.v-btn');
          
          if (!isClickOnMessage) {
            const rect = container.getBoundingClientRect();
            const scrollbarWidth = container.offsetWidth - container.clientWidth;
            const clickX = event.clientX - rect.left;
            const isScrollbarClick = clickX > container.clientWidth - scrollbarWidth;
            
            if (!isScrollbarClick) {
              console.log('🔵 MOUSEDOWN detected on empty space - setting up scroll prevention IMMEDIATELY', {
                firstClickHandled: this.firstClickHandled,
                preventFirstScroll: this.preventFirstScroll
              });
              
              this.firstClickHandled = true;
              
              const currentScrollTop = container.scrollTop;
              this.lastScrollPosition = currentScrollTop;
              
              container.scrollTop = currentScrollTop;
              
              this.preventFirstScroll = true;
              this.preventScroll = true;
              
              if (this.preventScrollSetter) {
                this.preventScrollSetter(true);
              }
              
              const immediateScrollPrevention = (e) => {
                const newScrollTop = container.scrollTop;
                if (newScrollTop !== currentScrollTop) {
                  console.log('🔴 MOUSEDOWN: Preventing scroll - restoring to', currentScrollTop, 'from', newScrollTop);
                  e.preventDefault();
                  e.stopPropagation();
                  e.stopImmediatePropagation();
                  container.scrollTop = currentScrollTop;
                  return false;
                }
              };
              
              container.addEventListener('scroll', immediateScrollPrevention, { passive: false, capture: true });
              
              this.firstClickScrollPreventionHandler = immediateScrollPrevention;
              
              const preventWheelDuringClick = (e) => {
                if (this.isWheelScrolling) {
                  return; // Allow wheel scrolling
                }
                if (this.preventFirstScroll) {
                  console.log('🔴 MOUSEDOWN: Blocking wheel event to prevent scroll during click');
                  e.preventDefault();
                  e.stopPropagation();
                  e.stopImmediatePropagation();
                  return false;
                }
              };
              container.addEventListener('wheel', preventWheelDuringClick, { passive: false, capture: true });
              this.firstClickWheelPreventionHandler = preventWheelDuringClick;
              
              const restoreScrollLoop = () => {
                if (this.preventFirstScroll && container.scrollTop !== currentScrollTop) {
                  container.scrollTop = currentScrollTop;
                }
                if (this.preventFirstScroll) {
                  this.firstClickScrollPreventionRAF = requestAnimationFrame(restoreScrollLoop);
                } else {
                  this.firstClickScrollPreventionRAF = null;
                }
              };
              this.firstClickScrollPreventionRAF = requestAnimationFrame(restoreScrollLoop);
              
              this.firstClickScrollPreventionInterval = setInterval(() => {
                if (this.preventFirstScroll && container.scrollTop !== currentScrollTop) {
                  container.scrollTop = currentScrollTop;
                }
              }, 1);
              
              setTimeout(() => {
                if (this.firstClickScrollPreventionHandler === immediateScrollPrevention) {
                  container.removeEventListener('scroll', immediateScrollPrevention, { capture: true });
                  this.firstClickScrollPreventionHandler = null;
                }
                if (this.firstClickWheelPreventionHandler === preventWheelDuringClick) {
                  container.removeEventListener('wheel', preventWheelDuringClick, { capture: true });
                  this.firstClickWheelPreventionHandler = null;
                }
                if (this.firstClickScrollPreventionInterval) {
                  clearInterval(this.firstClickScrollPreventionInterval);
                  this.firstClickScrollPreventionInterval = null;
                }
                if (this.firstClickScrollPreventionRAF !== null) {
                  cancelAnimationFrame(this.firstClickScrollPreventionRAF);
                  this.firstClickScrollPreventionRAF = null;
                }
                this.preventFirstScroll = false;
                this.preventScroll = false;
                if (this.preventScrollSetter) {
                  this.preventScrollSetter(false);
                }
                console.log('🟢 Scroll prevention disabled after mousedown - normal scrolling now allowed');
              }, 800);
            }
          }
        }
        
        const isClickOnMessage = target.closest('.chat-bubble') || 
                                 target.closest('.v-list-item') ||
                                 target.closest('.v-list-item-content') ||
                                 target.closest('.studio-plot-link') ||
                                 target.closest('.visualization-prompt') ||
                                 target.closest('.html-content') ||
                                 target.closest('.embedded-studio-container') ||
                                 target.closest('.embedded-studio-content') ||
                                 target.closest('.embedded-studio-footer') ||
                                 target.closest('a') ||
                                 target.closest('button') ||
                                 target.closest('img') ||
                                 target.closest('input') ||
                                 target.closest('select') ||
                                 target.closest('textarea') ||
                                 target.closest('.v-select') ||
                                 target.closest('.v-text-field') ||
                                 target.closest('.v-btn');
        
        if (!isClickOnMessage) {
          const rect = container.getBoundingClientRect();
          const scrollbarWidth = container.offsetWidth - container.clientWidth;
          const clickX = event.clientX - rect.left;
          const isScrollbarClick = clickX > container.clientWidth - scrollbarWidth;
          
          if (!isScrollbarClick) {
            const isDirectContainerClick = target === container || 
                                           (target.classList && target.classList.contains('chat-history')) ||
                                           (target.classList && target.classList.contains('v-list') && !target.closest('.v-list-item'));
            
            if (isDirectContainerClick) {
              event.preventDefault();
              event.stopPropagation();
              event.stopImmediatePropagation();
              return false;
            }
          }
        }
      }
    },
    handleNativeClick(event) {
      if (!this.firstClickHandled) {
        this.firstClickHandled = true;
      }
      
      const target = event.target;
      const container = this.$refs.chatHistory;
      
      if (!container) return;
      
      if (target === container || (target.classList && target.classList.contains('chat-history'))) {
        const rect = container.getBoundingClientRect();
        const scrollbarWidth = container.offsetWidth - container.clientWidth;
        const clickX = event.clientX - rect.left;
        const isScrollbarClick = clickX > container.clientWidth - scrollbarWidth - 5; // Add 5px buffer
        
        if (isScrollbarClick) {
          console.log('🔵 SCROLLBAR MOUSEDOWN DETECTED in handleNativeMouseDown - disabling all scroll prevention');
          
          this.isScrollbarInteraction = true;
          this.preventFirstScroll = false;
          this.preventScroll = false;
          this.studioInteractionActive = false;
          
          if (this.firstClickScrollPreventionInterval) {
            clearInterval(this.firstClickScrollPreventionInterval);
            this.firstClickScrollPreventionInterval = null;
          }
          if (this.firstClickScrollPreventionRAF !== null) {
            cancelAnimationFrame(this.firstClickScrollPreventionRAF);
            this.firstClickScrollPreventionRAF = null;
          }
          if (this.firstClickScrollPreventionHandler) {
            container.removeEventListener('scroll', this.firstClickScrollPreventionHandler, { capture: true });
            this.firstClickScrollPreventionHandler = null;
          }
          
          if (this.scrollbarInteractionTimeout) {
            clearTimeout(this.scrollbarInteractionTimeout);
          }
          this.scrollbarInteractionTimeout = setTimeout(() => {
            this.isScrollbarInteraction = false;
          }, 3000);
          
          return; // Allow scrollbar mousedown - don't block it
        }
        
        if (!isScrollbarClick) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          return false;
        }
      }
      
      if (target.classList && target.classList.contains('v-list')) {
        const isOnListItem = target.closest('.v-list-item');
        if (!isOnListItem) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          return false;
        }
      }
    },
    handleNativeMouseDown(event) {
      const target = event.target;
      const container = this.$refs.chatHistory;
      
      if (!container) return;
      
      const isClickOnEmbeddedStudio = target.closest('.embedded-studio-container') || 
                                     target.closest('.embedded-studio-content') ||
                                     target.closest('.embedded-studio-footer');
      
      if (isClickOnEmbeddedStudio) {
        
        
        this.preventFirstScroll = false;
        this.studioInteractionActive = true;
        this.preventScroll = false;
        
        if (this.firstClickScrollPreventionInterval) {
          clearInterval(this.firstClickScrollPreventionInterval);
          this.firstClickScrollPreventionInterval = null;
        }
        if (this.firstClickScrollPreventionRAF !== null) {
          cancelAnimationFrame(this.firstClickScrollPreventionRAF);
          this.firstClickScrollPreventionRAF = null;
        }
        if (this.firstClickScrollPreventionHandler) {
          container.removeEventListener('scroll', this.firstClickScrollPreventionHandler, { capture: true });
          this.firstClickScrollPreventionHandler = null;
        }
        if (this.firstClickWheelPreventionHandler) {
          container.removeEventListener('wheel', this.firstClickWheelPreventionHandler, { capture: true });
          this.firstClickWheelPreventionHandler = null;
        }
        
        this.disableScrollPreventionForStudio();
        
        return; // Allow the mousedown to proceed normally
      }
      
      if (target === container || (target.classList && target.classList.contains('chat-history'))) {
        const rect = container.getBoundingClientRect();
        const scrollbarWidth = container.offsetWidth - container.clientWidth;
        const clickX = event.clientX - rect.left;
        const isScrollbarClick = clickX > container.clientWidth - scrollbarWidth;
        
        if (!isScrollbarClick) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          return false;
        }
      }
      
      if (target.classList && target.classList.contains('v-list')) {
        const isOnListItem = target.closest('.v-list-item');
        if (!isOnListItem) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          return false;
        }
      }
    },
    handleNativeMouseUp(event) {
      const target = event.target;
      const container = this.$refs.chatHistory;
      
      if (!container) return;
      
      if (target === container || (target.classList && target.classList.contains('chat-history'))) {
        const rect = container.getBoundingClientRect();
        const scrollbarWidth = container.offsetWidth - container.clientWidth;
        const clickX = event.clientX - rect.left;
        const isScrollbarClick = clickX > container.clientWidth - scrollbarWidth;
        
        if (!isScrollbarClick) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          return false;
        }
      }
      
      if (target.classList && target.classList.contains('v-list')) {
        const isOnListItem = target.closest('.v-list-item');
        if (!isOnListItem) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          return false;
        }
      }
    },
    handleChatClickCapture(event) {
      if (!this.firstClickHandled) {
        this.firstClickHandled = true;
      }
      
      const target = event.target;
      const container = this.$refs.chatHistory;
      
      if (!container) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        return false;
      }
      
      const isDirectContainerClick = target === container || 
                                     (target.classList && target.classList.contains('chat-history')) ||
                                     (target.classList && target.classList.contains('v-list') && !target.closest('.v-list-item'));
      
      if (isDirectContainerClick) {
        const rect = container.getBoundingClientRect();
        const scrollbarWidth = container.offsetWidth - container.clientWidth;
        const clickX = event.clientX - rect.left;
        const isScrollbarClick = clickX > container.clientWidth - scrollbarWidth;
        
        if (!isScrollbarClick) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          return false;
        }
      }
    },
    handleChatClick(event) {
      if (!this.firstClickHandled) {
        this.firstClickHandled = true;
      }
      
      const target = event.target;
      const isClickOnMessage = target.closest('.chat-bubble') || 
                               target.closest('.v-list-item') ||
                               target.closest('.v-list-item-content') ||
                               target.closest('.studio-plot-link') ||
                               target.closest('.visualization-prompt') ||
                               target.closest('.html-content') ||
                               target.closest('.embedded-studio-container') ||
                               target.closest('.embedded-studio-content') ||
                               target.closest('.embedded-studio-footer') ||
                               target.closest('a') ||
                               target.closest('button') ||
                               target.closest('img') ||
                               target.closest('input') ||
                               target.closest('select') ||
                               target.closest('textarea') ||
                               target.closest('.v-select') ||
                               target.closest('.v-text-field') ||
                               target.closest('.v-btn');
      
      if (isClickOnMessage) {
        if (target.closest('.embedded-studio-container') || target.closest('.embedded-studio-content')) {
          this.preventScroll = false;
          if (this.preventScrollSetter) {
            this.preventScrollSetter(false);
          }
        }
        return; // Allow the click for text selection
      }
      
      const container = this.$refs.chatHistory;
      if (!container) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        return false;
      }
      
      const rect = container.getBoundingClientRect();
      const scrollbarWidth = container.offsetWidth - container.clientWidth;
      const clickX = event.clientX - rect.left;
      const isScrollbarClick = clickX > container.clientWidth - scrollbarWidth;
      
      if (isScrollbarClick) {
        this.isScrollbarInteraction = true;
        this.preventFirstScroll = false;
        if (this.scrollbarInteractionTimeout) {
          clearTimeout(this.scrollbarInteractionTimeout);
        }
        this.scrollbarInteractionTimeout = setTimeout(() => {
          this.isScrollbarInteraction = false;
        }, 2000);
        return; // Allow scrollbar clicks
      }
      
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      return false;
    },
    handleMouseDownCapture(event) {
      const target = event.target;
      const container = this.$refs.chatHistory;
      
      if (!container) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        return false;
      }
      
      const isDirectContainerClick = target === container || 
                                     (target.classList && target.classList.contains('chat-history')) ||
                                     (target.classList && target.classList.contains('v-list') && !target.closest('.v-list-item'));
      
      if (isDirectContainerClick) {
        const rect = container.getBoundingClientRect();
        const scrollbarWidth = container.offsetWidth - container.clientWidth;
        const clickX = event.clientX - rect.left;
        const isScrollbarClick = clickX > container.clientWidth - scrollbarWidth;
        
        if (!isScrollbarClick) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          return false;
        }
      }
    },
    handleMouseDown(event) {
      const target = event.target;
      const isClickOnMessage = target.closest('.chat-bubble') || 
                               target.closest('.v-list-item') ||
                               target.closest('.v-list-item-content') ||
                               target.closest('.studio-plot-link') ||
                               target.closest('.visualization-prompt') ||
                               target.closest('.html-content') ||
                               target.closest('.embedded-studio-container') ||
                               target.closest('.embedded-studio-content') ||
                               target.closest('.embedded-studio-footer') ||
                               target.closest('a') ||
                               target.closest('button') ||
                               target.closest('img') ||
                               target.closest('input') ||
                               target.closest('select') ||
                               target.closest('textarea') ||
                               target.closest('.v-select') ||
                               target.closest('.v-text-field') ||
                               target.closest('.v-btn');
      
      if (isClickOnMessage) {
        if (target.closest('.embedded-studio-container') || target.closest('.embedded-studio-content')) {
          this.preventScroll = false;
          if (this.preventScrollSetter) {
            this.preventScrollSetter(false);
          }
        }
        return; // Allow mousedown for text selection
      }
      
      const container = this.$refs.chatHistory;
      if (!container) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      
      const rect = container.getBoundingClientRect();
      const scrollbarWidth = container.offsetWidth - container.clientWidth;
      const clickX = event.clientX - rect.left;
      const isScrollbarClick = clickX > container.clientWidth - scrollbarWidth;
      
      if (isScrollbarClick) {
        this.isScrollbarInteraction = true;
        this.preventFirstScroll = false;
        if (this.scrollbarInteractionTimeout) {
          clearTimeout(this.scrollbarInteractionTimeout);
        }
        this.scrollbarInteractionTimeout = setTimeout(() => {
          this.isScrollbarInteraction = false;
        }, 2000);
        return; // Allow scrollbar mousedown
      }
      
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      return false;
    },
    handleMouseUpCapture(event) {
      const target = event.target;
      const container = this.$refs.chatHistory;
      
      if (!container) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        return false;
      }
      
      const isDirectContainerClick = target === container || 
                                     (target.classList && target.classList.contains('chat-history')) ||
                                     (target.classList && target.classList.contains('v-list') && !target.closest('.v-list-item'));
      
      if (isDirectContainerClick) {
        const rect = container.getBoundingClientRect();
        const scrollbarWidth = container.offsetWidth - container.clientWidth;
        const clickX = event.clientX - rect.left;
        const isScrollbarClick = clickX > container.clientWidth - scrollbarWidth;
        
        if (!isScrollbarClick) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          return false;
        }
      }
    },
    handleMouseUp(event) {
      const target = event.target;
      const isClickOnMessage = target.closest('.chat-bubble') || 
                               target.closest('.v-list-item') ||
                               target.closest('.v-list-item-content') ||
                               target.closest('.studio-plot-link') ||
                               target.closest('.visualization-prompt') ||
                               target.closest('.html-content') ||
                               target.closest('.embedded-studio-container') ||
                               target.closest('.embedded-studio-content') ||
                               target.closest('.embedded-studio-footer') ||
                               target.closest('a') ||
                               target.closest('button') ||
                               target.closest('img') ||
                               target.closest('input') ||
                               target.closest('select') ||
                               target.closest('textarea') ||
                               target.closest('.v-select') ||
                               target.closest('.v-text-field') ||
                               target.closest('.v-btn');
      
      if (isClickOnMessage) {
        if (target.closest('.embedded-studio-container') || target.closest('.embedded-studio-content')) {
          this.preventScroll = false;
          if (this.preventScrollSetter) {
            this.preventScrollSetter(false);
          }
        }
        return; // Allow mouseup for text selection
      }
      
      const container = this.$refs.chatHistory;
      if (!container) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      
      const rect = container.getBoundingClientRect();
      const scrollbarWidth = container.offsetWidth - container.clientWidth;
      const clickX = event.clientX - rect.left;
      const isScrollbarClick = clickX > container.clientWidth - scrollbarWidth;
      
      if (isScrollbarClick) {
        this.isScrollbarInteraction = true;
        this.preventFirstScroll = false;
        if (this.scrollbarInteractionTimeout) {
          clearTimeout(this.scrollbarInteractionTimeout);
        }
        this.scrollbarInteractionTimeout = setTimeout(() => {
          this.isScrollbarInteraction = false;
        }, 2000);
        return; // Allow scrollbar mouseup
      }
      
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      return false;
    },
    handleMouseMove(event) {
      const container = this.$refs.chatHistory;
      if (!container) return;
      
      const rect = container.getBoundingClientRect();
      const scrollbarWidth = container.offsetWidth - container.clientWidth;
      const mouseX = event.clientX - rect.left;
      const isOverScrollbar = mouseX > container.clientWidth - scrollbarWidth;
      
      if (isOverScrollbar) {
        container.style.cursor = 'pointer';
        if (event.buttons === 1) {
          console.log('🔵 SCROLLBAR DRAG DETECTED in handleMouseMove - ensuring scroll prevention is disabled');
          
          this.isScrollbarInteraction = true;
          this.preventFirstScroll = false;
          this.preventScroll = false;
          this.studioInteractionActive = false;
          
          if (this.firstClickScrollPreventionInterval) {
            clearInterval(this.firstClickScrollPreventionInterval);
            this.firstClickScrollPreventionInterval = null;
          }
          if (this.firstClickScrollPreventionRAF !== null) {
            cancelAnimationFrame(this.firstClickScrollPreventionRAF);
            this.firstClickScrollPreventionRAF = null;
          }
          if (this.firstClickScrollPreventionHandler) {
            container.removeEventListener('scroll', this.firstClickScrollPreventionHandler, { capture: true });
            this.firstClickScrollPreventionHandler = null;
          }
          
          if (this.scrollbarInteractionTimeout) {
            clearTimeout(this.scrollbarInteractionTimeout);
          }
          this.scrollbarInteractionTimeout = setTimeout(() => {
            this.isScrollbarInteraction = false;
          }, 3000);
        }
      } else {
        container.style.cursor = 'text';
      }
    },
    handleWheel(event) {
      this.isWheelScrolling = true;
      
      if (this.wheelScrollTimeout) {
        clearTimeout(this.wheelScrollTimeout);
      }
      
      this.wheelScrollTimeout = setTimeout(() => {
        this.isWheelScrolling = false;
      }, 150); // Clear after 150ms of no wheel events
      
      if (this.preventFirstScroll || this.preventScroll) {
        this.clearScrollPreventionLocks();
      }
    },
    handleScroll(event) {
      const container = this.$refs.chatHistory;
      if (container) {
        const activeElement = document.activeElement;
        const isInEmbeddedStudio = activeElement && (
          activeElement.closest('.embedded-studio-container') ||
          activeElement.closest('.embedded-studio-content')
        );
        
        const hasEmbeddedStudio = container.querySelector('.embedded-studio-container');
        const mouseTarget = event.target;
        const isClickOnEmbeddedStudio = mouseTarget && (
          mouseTarget.closest('.embedded-studio-container') ||
          mouseTarget.closest('.embedded-studio-content')
        );
        
        if (isInEmbeddedStudio || isClickOnEmbeddedStudio || hasEmbeddedStudio) {
          if (this.preventFirstScroll || this.studioInteractionActive === false) {
            this.disableScrollPreventionForStudio();
          }
          this.lastScrollPosition = container.scrollTop;
          return;
        }
      }
      
      if (this.isScrollbarInteraction) {
        if (container) {
          this.lastScrollPosition = container.scrollTop;
        }
        return;
      }
      
      if (this.isWheelScrolling) {
        if (container) {
          this.lastScrollPosition = container.scrollTop;
        }
        if (this.preventFirstScroll || this.preventScroll) {
          this.preventFirstScroll = false;
          this.preventScroll = false;
          if (this.preventScrollSetter) {
            this.preventScrollSetter(false);
          }
        }
        return;
      }
      
      
      const currentScroll = container ? container.scrollTop : (event.target ? event.target.scrollTop : 0);
      
      if (container) {
        const hasEmbeddedStudio = container.querySelector('.embedded-studio-container');
        if (hasEmbeddedStudio) {
          const activeElement = document.activeElement;
          const isInEmbeddedStudio = activeElement && (
            activeElement.closest('.embedded-studio-container') ||
            activeElement.closest('.embedded-studio-content')
          );
          
          if (isInEmbeddedStudio || this.studioInteractionActive) {
            this.lastScrollPosition = container.scrollTop;
            this.preventScroll = false;
            this.preventFirstScroll = false;
            if (this.preventScrollSetter) {
              this.preventScrollSetter(false);
            }
            return;
          }
        }
      }
      
      const previousScrollPosition = this.lastScrollPosition;
      
      this.lastScrollPosition = currentScroll;
      
      if (container && !this.isScrollbarInteraction && !this.studioInteractionActive && previousScrollPosition !== undefined) {
        const scrollDelta = currentScroll - previousScrollPosition;
        const absDelta = Math.abs(scrollDelta);
        
        if (absDelta > 1) {
          if (this.autoResetScrollPreventionTimeout) {
            clearTimeout(this.autoResetScrollPreventionTimeout);
            this.autoResetScrollPreventionTimeout = null;
          }
          
          if (scrollDelta > 0) {
            if (this.preventFirstScroll || this.preventScroll) {
              console.log('🟢 User scrolled DOWN - clearing scroll prevention immediately');
              this.preventFirstScroll = false;
              this.preventScroll = false;
              if (this.preventScrollSetter) {
                this.preventScrollSetter(false);
              }
            }
          } else {
            if (this.preventFirstScroll || this.preventScroll) {
              this.clearScrollPreventionLocks();
            }
          }
        }
      }
    },
    adjustIframeHeight(event) {
      const iframe = event.target;
      try {
        const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
        if (iframeDoc) {
          const height = iframeDoc.body.scrollHeight;
          if (height > 0) {
            iframe.style.height = Math.min(height + 20, 600) + 'px'; // Max height of 600px
          }
        }
      } catch (error) {
        iframe.style.height = '400px';
      }
    },
    handlePlotLoad(index) {
      if (this.history[index]) {
        this.history[index].plotLoading = false;
        this.history[index].plotError = false;
        this.$nextTick(() => {
          const chatHistory = this.$refs.chatHistory;
          if (chatHistory) {
            const iframes = chatHistory.querySelectorAll('iframe');
            const targetIframe = Array.from(iframes).find(iframe => 
              iframe.src === this.history[index].plotUrl || 
              iframe.src.includes(this.history[index].plotUrl)
            );
            if (targetIframe) {
              try {
                const iframeDoc = targetIframe.contentDocument || targetIframe.contentWindow.document;
                if (iframeDoc && iframeDoc.body) {
                  this.adjustIframeHeight({ target: targetIframe });
                } else {
                  targetIframe.style.height = '400px';
                }
              } catch (error) {
                targetIframe.style.height = '400px';
              }
            }
          }
        });
      }
    },
    handlePlotError(index) {
      if (this.history[index]) {
        this.history[index].plotLoading = false;
        this.history[index].plotError = true;
      }
    },
    getPlotName(content) {
      try {
        const contentObj = typeof content === 'string' ? JSON.parse(content) : content;
        if (contentObj && contentObj.api_payload && contentObj.api_payload.name) {
          return contentObj.api_payload.name;
        }
        return 'View Plot';
      } catch (error) {
        console.error('Error parsing plot name:', error);
        return 'View Plot';
      }
    },
    getStudioPlotUrl(content) {
      try {
        const contentObj = typeof content === 'string' ? JSON.parse(content) : content;
        if (contentObj && contentObj.plotId) {
          return `/${this.tenantId}/Fluidsdata-studio?plotId=${contentObj.plotId}`;
        }
        return `/${this.tenantId}/Fluidsdata-studio`;
      } catch (error) {
        console.error('Error generating studio URL:', error);
        return `/${this.tenantId}/Fluidsdata-studio`;
      }
    },
    openStudioWithPlot(content) {
      try {
        const contentObj = typeof content === 'string' ? JSON.parse(content) : content;
        
        if (contentObj && contentObj.plotId && contentObj.api_payload) {
          const plotData = {
            ID: contentObj.plotId,
            Name: contentObj.api_payload.name,
            ChartType: contentObj.api_payload.ChartType,
            TableType: contentObj.api_payload.TableType,
            XAxisColumn: contentObj.api_payload.XAxisColumn,
            YAxisColumns: contentObj.api_payload.YAxisColumns,
            ZAxisColumn: contentObj.api_payload.ZAxisColumn,
            GroupColumn: contentObj.api_payload.GroupColumn,
          };
          
          localStorage.setItem('savedChartData', JSON.stringify(plotData));
          
          const url = `/${this.tenantId}/Fluidsdata-studio?plotId=${contentObj.plotId}`;
          this.$router.push(url);
        } else {
          const url = `/${this.tenantId}/Fluidsdata-studio`;
          this.$router.push(url);
        }
      } catch (error) {
        console.error('Error opening studio with plot:', error);
        const url = `/${this.tenantId}/Fluidsdata-studio`;
        this.$router.push(url);
      }
    },
    hasVisualizationData(msg) {
      if (msg.xAxis && msg.yAxis && msg.legend) {
        return true;
      }
      
      if (msg.metadata) {
        const xAxis = msg.metadata.xaxis || msg.metadata.xAxis || msg.metadata.XAxis;
        const yAxis = msg.metadata.yaxis || msg.metadata.yAxis || msg.metadata.YAxis;
        const legend = msg.metadata.legend || msg.metadata.Legend;
        if (xAxis && yAxis && legend) {
          return true;
        }
      }
      
      try {
        const content = typeof msg.context === 'string' ? JSON.parse(msg.context) : msg.context;
        if (content && (content.xaxis || content.xAxis || content.XAxis) && 
            (content.yaxis || content.yAxis || content.YAxis) && 
            (content.legend || content.Legend)) {
          return true;
        }
      } catch (error) {
        void 0;
      }
      
      return false;
    },
    getVisualizationData(msg) {
      
      if (msg.metadata) {
        const xaxis = this.message.xaxis || msg.xAxis || msg.XAxis;
        const yaxis = msg.yaxis || msg.yAxis || msg.YAxis;
        const legend = msg.legend || msg.Legend;
        if (xaxis && yaxis && legend) {
          return { xaxis, yaxis, legend };
        }
      }
      
      if (msg.xAxis && msg.yAxis && msg.legend) {
        return {
          xaxis: msg.xAxis,
          yaxis: msg.yAxis,
          legend: msg.legend
        };
      }
      
      try {
        const content = typeof msg.context === 'string' ? JSON.parse(msg.context) : msg.context;
        if (content) {
          const xaxis = content.xaxis || content.xAxis || content.XAxis;
          const yaxis = content.yaxis || content.yAxis || content.YAxis;
          const legend = content.legend || content.Legend;
          if (xaxis && yaxis && legend) {
            return { xaxis, yaxis, legend };
          }
        }
      } catch (error) {
        void 0;
      }
      
      return { xaxis: '', yaxis: '', legend: '' };
    },
    handleVisualizationYes(index) {
      const msg = this.history[index];
      const vizData = this.getVisualizationData(msg);
      
      this.history[index].visualizationResponded = true;
       const prompt = `Yes, create the scatter plot for Cross test, for ${vizData.yaxis} VS ${vizData.xaxis} with respect to ${vizData.legend} in the studio.`;
      
      this.message = prompt;
      this.sendMessage();
    },
    handleVisualizationNo(index) {
      this.history[index].visualizationResponded = true;
    },
    handleStudioYes(index) {
      const msg = this.history[index];
      if (msg) {
        this.history[index].studioResponded = true;
        this.history[index].studioEmbedded = true;
        
        this.$nextTick(() => {
          if (this.firstClickScrollPreventionInterval) {
            clearInterval(this.firstClickScrollPreventionInterval);
            this.firstClickScrollPreventionInterval = null;
          }
          if (this.firstClickScrollPreventionRAF !== null) {
            cancelAnimationFrame(this.firstClickScrollPreventionRAF);
            this.firstClickScrollPreventionRAF = null;
          }
          if (this.firstClickScrollPreventionHandler) {
            const container = this.$refs.chatHistory;
            if (container) {
              container.removeEventListener('scroll', this.firstClickScrollPreventionHandler, { capture: true });
            }
            this.firstClickScrollPreventionHandler = null;
          }
          if (this.firstClickWheelPreventionHandler) {
            const container = this.$refs.chatHistory;
            if (container) {
              container.removeEventListener('wheel', this.firstClickWheelPreventionHandler, { capture: true });
            }
            this.firstClickWheelPreventionHandler = null;
          }
          
          this.studioInteractionActive = true;
          this.preventScroll = false;
          this.preventFirstScroll = false;
          if (this.preventScrollSetter) {
            this.preventScrollSetter(false);
          }
          
        });
      }
    },
    handleStudioNo(index) {
      const msg = this.history[index];
      if (msg) {
        this.history[index].studioResponded = true;
        this.history[index].studioEmbedded = false;
      }
    },
    closeEmbeddedStudio(index) {
      const msg = this.history[index];
      if (msg) {
        this.history[index].studioEmbedded = false;
      }
    },
    getPlotIdFromContext(content) {
      try {
        const contentObj = typeof content === 'string' ? JSON.parse(content) : content;
        if (contentObj && contentObj.plotId) {
          return contentObj.plotId;
        }
        return null;
      } catch (error) {
        console.error('Error parsing plot ID:', error);
        return null;
      }
    },
    openStudioInNewPage(content) {
      try {
        const contentObj = typeof content === 'string' ? JSON.parse(content) : content;
        
        if (contentObj && contentObj.plotId && contentObj.api_payload) {
          const plotData = {
            ID: contentObj.plotId,
            Name: contentObj.api_payload.name,
            ChartType: contentObj.api_payload.ChartType,
            TableType: contentObj.api_payload.TableType,
            XAxisColumn: contentObj.api_payload.XAxisColumn,
            YAxisColumns: contentObj.api_payload.YAxisColumns,
            ZAxisColumn: contentObj.api_payload.ZAxisColumn,
            GroupColumn: contentObj.api_payload.GroupColumn,
          };
          
          localStorage.setItem('savedChartData', JSON.stringify(plotData));
          
          const url = `/${this.tenantId}/Fluidsdata-studio?plotId=${contentObj.plotId}`;
          this.$router.push(url);
        } else {
          const url = `/${this.tenantId}/Fluidsdata-studio`;
          this.$router.push(url);
        }
      } catch (error) {
        console.error('Error opening studio in new page:', error);
        const url = `/${this.tenantId}/Fluidsdata-studio`;
        this.$router.push(url);
      }
    },
    disableScrollPreventionForStudio() {
      const container = this.$refs.chatHistory;
      
      if (container) {
        const currentScrollTop = container.scrollTop;
        this.lastScrollPosition = currentScrollTop;
        
        requestAnimationFrame(() => {
          if (container && container.scrollTop !== currentScrollTop) {
            container.scrollTop = currentScrollTop;
          }
        });
        
        const lockScrollHandler = (e) => {
          if (container.scrollTop !== currentScrollTop) {
            e.preventDefault();
            e.stopPropagation();
            container.scrollTop = currentScrollTop;
            container.removeEventListener('scroll', lockScrollHandler, { capture: true });
          }
        };
        container.addEventListener('scroll', lockScrollHandler, { passive: false, capture: true, once: true });
      }
      
      this.studioInteractionActive = true;
      this.preventScroll = false;
      this.preventFirstScroll = false; // CRITICAL: Disable first click scroll prevention
      
      if (this.preventScrollSetter) {
        this.preventScrollSetter(false);
      }
      
      if (this.firstClickScrollPreventionInterval) {
        clearInterval(this.firstClickScrollPreventionInterval);
        this.firstClickScrollPreventionInterval = null;
      }
      if (this.firstClickScrollPreventionRAF !== null) {
        cancelAnimationFrame(this.firstClickScrollPreventionRAF);
        this.firstClickScrollPreventionRAF = null;
      }
      if (this.firstClickScrollPreventionHandler) {
        if (container) {
          container.removeEventListener('scroll', this.firstClickScrollPreventionHandler, { capture: true });
        }
        this.firstClickScrollPreventionHandler = null;
      }
      if (this.firstClickWheelPreventionHandler) {
        if (container) {
          container.removeEventListener('wheel', this.firstClickWheelPreventionHandler, { capture: true });
        }
        this.firstClickWheelPreventionHandler = null;
      }
      
      
      if (this.studioInteractionTimeout) {
        clearTimeout(this.studioInteractionTimeout);
      }
      
      this.studioInteractionTimeout = setTimeout(() => {
        const container = this.$refs.chatHistory;
        if (container) {
          const activeElement = document.activeElement;
          const isInEmbeddedStudio = activeElement && (
            activeElement.closest('.embedded-studio-container') ||
            activeElement.closest('.embedded-studio-content')
          );
          if (!isInEmbeddedStudio) {
            this.studioInteractionActive = false;
          } else {
            this.studioInteractionTimeout = setTimeout(() => {
              this.studioInteractionActive = false;
            }, 2000);
          }
        } else {
          this.studioInteractionActive = false;
        }
      }, 2000);
    },
    handleGlobalFocus(event) {
      const target = event.target;
      if (target && (target.closest('.embedded-studio-container') || target.closest('.embedded-studio-content'))) {
        this.disableScrollPreventionForStudio();
        
        if (target.scrollIntoView) {
          const originalScrollIntoView = target.scrollIntoView;
          target.scrollIntoView = function() {
            void 0;
          };
          setTimeout(() => {
            target.scrollIntoView = originalScrollIntoView;
          }, 100);
        }
      }
    },
  },
  watch: {
    uploadedFile(newFile) {
      if (newFile && !this.isUploading) {
        this.localUploadedFile = newFile; // Update local file reference
      } else if (newFile === "" || newFile === null) {
        this.clearPDFState();
      }
    },
    message(newMessage) {
      void 0;
    },
    history: {
      handler(newHistory, oldHistory) {
        if (newHistory && newHistory.length > 0) {
          if (!this.isInitialLoad && (!oldHistory || newHistory.length > oldHistory.length)) {
            this.$nextTick(() => {
              this.forceScrollToBottom();
            });
          }
          
          const hasEmbeddedStudio = newHistory.some(msg => msg.studioEmbedded === true);
          if (hasEmbeddedStudio) {
            this.$nextTick(() => {
              this.studioInteractionActive = true;
              this.preventScroll = false;
              this.preventFirstScroll = false;
              if (this.preventScrollSetter) {
                this.preventScrollSetter(false);
              }
            });
          }
        }
      },
      deep: true
    }
  },
  mounted() {
    this.getHistory(); // Call getHistory on mount
    window.addEventListener('resize', this.handleResize);
    
    document.addEventListener('focusin', this.handleGlobalFocus, true);
    
    document.addEventListener('click', this.handleDocumentClick, true);
    document.addEventListener('mousedown', this.handleDocumentMouseDown, true);
    
    this.$nextTick(() => {
      const chatHistoryEl = this.$refs.chatHistory;
      if (chatHistoryEl) {
        chatHistoryEl.addEventListener('click', this.handleNativeClick, true);
        chatHistoryEl.addEventListener('mousedown', this.handleNativeMouseDown, true);
        chatHistoryEl.addEventListener('mouseup', this.handleNativeMouseUp, true);
      }
    });
    
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
    document.removeEventListener('focusin', this.handleGlobalFocus, true);
    
    document.removeEventListener('click', this.handleDocumentClick, true);
    document.removeEventListener('mousedown', this.handleDocumentMouseDown, true);
    
    const chatHistoryEl = this.$refs.chatHistory;
    if (chatHistoryEl) {
      chatHistoryEl.removeEventListener('click', this.handleNativeClick, true);
      chatHistoryEl.removeEventListener('mousedown', this.handleNativeMouseDown, true);
      chatHistoryEl.removeEventListener('mouseup', this.handleNativeMouseUp, true);
    }
    
    if (this.selectionTimeout) {
      clearTimeout(this.selectionTimeout);
    }
    if (this.scrollPreventionTimeout) {
      clearTimeout(this.scrollPreventionTimeout);
    }
    if (this.autoResetScrollPreventionTimeout) {
      clearTimeout(this.autoResetScrollPreventionTimeout);
    }
    if (this.studioInteractionTimeout) {
      clearTimeout(this.studioInteractionTimeout);
    }
    if (this.scrollbarInteractionTimeout) {
      clearTimeout(this.scrollbarInteractionTimeout);
    }
    if (this.wheelScrollTimeout) {
      clearTimeout(this.wheelScrollTimeout);
    }
  }
};
</script>
